/*!
 * RUN IT LONDON — blackjack reward popup controller.
 *
 * One explicit state machine drives the DOM. Everything the shopper sees is
 * rendered from config (see the JSON block emitted by
 * sections/runit-blackjack-popup.liquid) plus the state the server returned.
 *
 * Security note: this file never decides a discount percentage and never holds
 * a credential. It posts the game result to the claim endpoint and renders what
 * comes back, after checking the response against the allow-list in
 * runit-blackjack-core.js.
 */
(function () {
  'use strict';

  var Core = window.RunItBlackjackCore;
  if (!Core) return;

  var STATES = {
    CLOSED: 'closed',
    READY: 'ready',
    DEALING: 'dealing',
    PLAYER_TURN: 'player_turn',
    DEALER_TURN: 'dealer_turn',
    RESULT: 'result',
    EMAIL_CAPTURE: 'email_capture',
    CLAIMING: 'claiming',
    REWARD_REVEAL: 'reward_reveal',
    DOUBLE_DOWN: 'double_down',
    SMS_SUBMITTING: 'sms_submitting',
    MYSTERY_REVEAL: 'mystery_reveal',
    FINAL: 'final',
    ERROR: 'error'
  };

  var REDUCED =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Timing budget: quick and premium, collapsed for reduced-motion users. */
  var SPEED = {
    card: REDUCED ? 30 : 330,
    flip: REDUCED ? 30 : 440,
    step: REDUCED ? 30 : 180,
    settle: REDUCED ? 30 : 150,
    reveal: REDUCED ? 30 : 240,
    autoAdvance: REDUCED ? 400 : 1700
  };

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;

  var EVENT_PREFIX = 'runit_blackjack_';

  function now() {
    return Date.now();
  }

  /* ------------------------------------------------------------------ *
   * Analytics — never required, never throwing, never carrying PII.
   * ------------------------------------------------------------------ */

  function track(name, props) {
    var detail = props || {};
    try {
      document.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
    } catch (e) {
      /* noop */
    }
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        var payload = { event: name };
        for (var key in detail) {
          if (Object.prototype.hasOwnProperty.call(detail, key)) payload[key] = detail[key];
        }
        window.dataLayer.push(payload);
      }
    } catch (e) {
      /* noop */
    }
    try {
      if (window.RunItAnalytics && typeof window.RunItAnalytics.capture === 'function') {
        window.RunItAnalytics.capture(name, detail);
      }
    } catch (e) {
      /* noop */
    }
  }

  /* ------------------------------------------------------------------ *
   * Small helpers
   * ------------------------------------------------------------------ */

  /** "{percent}% OFF" -> "15% OFF". Unknown tokens are left alone. */
  /* " OVER £100" for a percentage that needs a minimum order, else "". The
     code itself carries the minimum at checkout; this only says so up front. */
  function minimumNote(percent, before, after) {
    var min = (Core.MIN_SUBTOTAL_BY_PERCENT || {})[percent];
    return min ? before + min + (after || '') : '';
  }

  function fmt(template, vars) {
    if (typeof template !== 'string') return '';
    return template.replace(/\{(\w+)\}/g, function (match, key) {
      return vars && Object.prototype.hasOwnProperty.call(vars, key) && vars[key] !== null && vars[key] !== undefined
        ? String(vars[key])
        : match;
    });
  }

  function wait(ms) {
    return new Promise(function (resolve) {
      window.setTimeout(resolve, ms);
    });
  }

  function clampPercent(percent, fallback) {
    return typeof percent === 'number' && isFinite(percent) && percent > 0 && percent <= 100
      ? Math.round(percent)
      : fallback;
  }

  /**
   * Read a mobile number the way a shopper actually types one.
   *
   * Two things went wrong on the way to Shopify before this existed: `07123
   * 456789` was sent as `07123456789` (the national format the Admin API refuses
   * — it wants E.164), and nothing was ever rejected locally, so every mistake
   * was reported as "we couldn't add that number" after a round trip.
   *
   * Accepted: UK national (`07123 456789`), UK international (`+44 7123 456789`),
   * the `00` international prefix (`0044…`), a `44…` written without its `+`, the
   * redundant `+44 (0)…` form, and any other country's `+<code>` number.
   *
   * Rejected, and *locally*, because a round trip to be told the shopper's number
   * is wrong is a worse experience than being told immediately: letters, anything
   * outside 7–15 digits, a bare national number with no country we can safely
   * infer, and — because this field collects consent to send TEXT MARKETING — any
   * `+44` number that is not in the UK mobile range. `+44 20 7946 0958` is a
   * London landline: it looks like a British number, it is a perfectly good
   * telephone number, and it cannot receive a marketing text. Treating every
   * `+44` number as a mobile is the mistake this rule exists to avoid.
   *
   * `reason` is what lets the popup tell the shopper which of those they missed
   * rather than showing one generic sentence for all of them.
   */
  function normalisePhone(value) {
    var raw = String(value || '').trim();
    if (!raw) return { raw: raw, digits: '', e164: '', valid: false, reason: 'empty' };
    if (/[A-Za-z]/.test(raw)) return { raw: raw, digits: '', e164: '', valid: false, reason: 'letters' };

    var digits = raw.replace(/[^\d]/g, '');
    var international = raw.charAt(0) === '+';

    /* `00` is how most of the world outside North America writes a `+`. */
    if (!international && digits.indexOf('00') === 0) {
      digits = digits.slice(2);
      international = true;
    }

    /* A leading zero is a NATIONAL number: `07…` is a UK mobile, and Shopify
       wants it as `+447…`. Only convert when what follows the zero looks like a
       full national number, so a half-typed `07` is still just invalid. */
    if (!international && digits.charAt(0) === '0' && digits.length >= 10 && digits.length <= 11) {
      digits = '44' + digits.slice(1);
      international = true;
    }

    /* `447123456789` — a UK number typed without its `+`. */
    if (!international && digits.indexOf('44') === 0 && digits.length === 12) {
      international = true;
    }

    /* `+44 (0)7123 456789` — the trunk zero written redundantly after the country
       code. Common enough on a UK site that refusing it would read as a bug. */
    if (digits.indexOf('44') === 0 && digits.charAt(2) === '0') {
      digits = '44' + digits.slice(3);
    }

    var e164 = international ? '+' + digits : digits;
    /* E.164 allows at most 15 digits, and no national number is shorter than 7. */
    if (!/^[1-9]\d{6,14}$/.test(digits)) {
      return { raw: raw, digits: digits, e164: e164, valid: false, reason: 'length' };
    }

    /* A `44` number has to be a UK MOBILE, and `07…` alone does not prove that.
       Ofcom gives mobile to 071–075 and 077–079; 070 is personal numbering (a
       follow-me number) and 076 is paging, and neither can receive this store's
       marketing texts. So: `+44 7`, then 1–5 or 7–9, then eight digits. Keep
       this identical to `normalisePhone()` in the Worker — if the two disagree,
       the browser accepts a number the backend then refuses. */
    if (digits.indexOf('44') === 0 && !/^447[1-57-9][0-9]{8}$/.test(digits)) {
      return { raw: raw, digits: digits, e164: e164, valid: false, reason: 'not_mobile' };
    }

    /* A bare national number whose country we cannot safely guess is not one we
       will store as somebody's marketing number. */
    if (!international) {
      return { raw: raw, digits: digits, e164: e164, valid: false, reason: 'country' };
    }

    return { raw: raw, digits: digits, e164: e164, valid: true, reason: '' };
  }

  function postJson(url, body) {
    return window
      .fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
        credentials: 'same-origin'
      })
      .then(function (response) {
        return response
          .text()
          .then(function (text) {
            var data = null;
            try {
              data = text ? JSON.parse(text) : null;
            } catch (e) {
              data = null;
            }
            return { ok: response.ok, status: response.status, data: data };
          })
          .catch(function () {
            return { ok: response.ok, status: response.status, data: null };
          });
      });
  }

  /* ------------------------------------------------------------------ *
   * Game
   * ------------------------------------------------------------------ */

  function createGame(root) {
    /* The config and the native form live inside the root, so a page that
       renders both the popup and the inline section keeps them separate. */
    var configNode = root.querySelector('[data-runit-bj-config]');
    if (!configNode) return null;

    var inline = root.hasAttribute('data-runit-bj-inline');

    var config;
    try {
      config = JSON.parse(configNode.textContent);
    } catch (e) {
      return null;
    }

    var q = function (selector) {
      return root.querySelector(selector);
    };
    var qa = function (selector) {
      return Array.prototype.slice.call(root.querySelectorAll(selector));
    };

    var el = {
      dialog: q('[data-runit-bj-dialog]'),
      overlay: q('[data-runit-bj-overlay]'),
      scroll: q('[data-runit-bj-scroll]'),
      close: q('[data-runit-bj-close]'),
      title: q('[data-runit-bj-title]'),
      sub: q('[data-runit-bj-sub]'),
      kicker: q('[data-runit-bj-kicker]'),
      dealerHand: q('[data-runit-bj-dealer-hand]'),
      playerHand: q('[data-runit-bj-player-hand]'),
      dealerTotal: q('[data-runit-bj-dealer-total]'),
      playerTotal: q('[data-runit-bj-player-total]'),
      hit: q('[data-runit-bj-hit]'),
      stand: q('[data-runit-bj-stand]'),
      live: q('[data-runit-bj-live]'),
      flow: q('[data-runit-bj-flow]'),
      /* Deliberately NOT a Shopify form: a `{% form 'customer' %}` here is what made
         the storefront-forms provider render an hCaptcha challenge over the CLAIM
         button. The submit is ours, and the consent goes to the rewards worker. */
      form: q('#RlBjClaim') || q('[data-runit-bj-form]'),
      email: q('[data-runit-bj-email]'),
      claim: q('[data-runit-bj-claim]'),
      claimLabel: q('[data-runit-bj-claim-label]'),
      message: q('[data-runit-bj-message]'),
      reward: q('[data-runit-bj-reward]'),
      resultKicker: q('[data-runit-bj-result-kicker]'),
      dealAgain: q('[data-runit-bj-deal-again]'),
      decline: q('[data-runit-bj-decline]'),
      codeKicker: q('[data-runit-bj-code-kicker]'),
      codeValue: q('[data-runit-bj-code]'),
      codeMeta: q('[data-runit-bj-code-meta]'),
      codeBox: q('[data-runit-bj-code-box]'),
      finalCodeBox: q('[data-runit-bj-final-code-box]'),
      codeNote: q('[data-runit-bj-code-note]'),
      codeActions: q('[data-runit-bj-code-actions]'),
      copy: q('[data-runit-bj-copy]'),
      ddStart: q('[data-runit-bj-dd-start]'),
      ddLocked: q('[data-runit-bj-dd-locked]'),
      mysteryCard: q('[data-runit-bj-mystery-card]'),
      mysteryReward: q('[data-runit-bj-mystery-reward]'),
      finalMeta: q('[data-runit-bj-final-meta]'),
      phoneOut: q('[data-runit-bj-phone-out]'),
      smsForm: q('[data-runit-bj-sms-form]'),
      phone: q('[data-runit-bj-phone]'),
      smsSubmit: q('[data-runit-bj-sms-submit]'),
      smsMessage: q('[data-runit-bj-sms-message]'),
      ddSkip: q('[data-runit-bj-dd-skip]'),
      finalSub: q('[data-runit-bj-final-sub]'),
      finalCode: q('[data-runit-bj-final-code]'),
      finalCopy: q('[data-runit-bj-final-copy]'),
      /* Play again — a second hand for fun, offered only once the shopper's
         reward is already theirs. */
      playAgain: q('[data-runit-bj-play-again]'),
      playAgainLabel: q('[data-runit-bj-play-again-label]'),
      playAgainNote: q('[data-runit-bj-play-again-note]'),
      playAgainResult: q('[data-runit-bj-play-again-result]'),
      tab: inline ? null : document.querySelector('[data-runit-bj-tab]'),
      tabText: inline ? null : document.querySelector('[data-runit-bj-tab-text]'),
      cardTpl: root.querySelector('[data-runit-bj-card-template]'),
      cardBackTpl: root.querySelector('[data-runit-bj-card-template-back]')
    };

    if (!el.dialog || !el.cardTpl) return null;

    var blocks = {};
    qa('[data-runit-bj-block]').forEach(function (node) {
      blocks[node.getAttribute('data-runit-bj-block')] = node;
    });

    var cardFrontHtml = el.cardTpl.innerHTML;
    var cardBackHtml = el.cardBackTpl ? el.cardBackTpl.innerHTML : cardFrontHtml;

    var shopUrl = config.shopUrl || '/collections/all';

    /* The face-down card, before the server has turned it over.

       The worker does not send the hole card's rank or suit while the hand is
       live, because a shopper who could read it in the Network tab would know
       whether to hit or stand — which is the same thing as picking their own
       reward. So the back is rendered with a neutral face and the real card is
       written in at the flip. */
    var HOLE_CARD = { rank: 'A', suit: 'spades' };

    var game = {
      state: STATES.CLOSED,
      /* The worker's id for the hand on the table. Every card, total and reward
         comes from the session this names; nothing about the hand is decided
         here. */
      sessionId: null,
      player: [],
      dealer: [],
      dealerHiddenEl: null,
      outcome: null,
      tier: null,
      percent: null,
      code: null,
      /* True while a hand is being played FOR FUN: the server dealt it as a
         replay, so the reward is not re-decided at the end of it — the claim
         the shopper already holds is read back instead. */
      replayLive: false,
      /* The reward the shopper held when the replay began, frozen before the new
         hand can put its own percentage anywhere near this one. */
      replayClaim: null,
      /* Whether the BIG play-again button is the replay offer on this screen.
         Held as state rather than read back off the button, because a screen can
         be built before either control is touched: the links row has to know
         what the other one is going to say, not what it happened to say last. */
      playAgainOffered: false,
      busy: false,
      lastFocus: null,
      autoTimer: null,
      advanceTimer: null,
      hardDismissed: false,
      /* Temporarily stepped aside for a higher-priority overlay. Pure UI state:
         never stored, never counted as a dismissal. */
      suspended: false,
      suspendedBy: null,
      /* A first-visit cookie screen outranks an unsolicited auto-open: once the
         shopper has answered it, the game offers its pill and waits to be asked
         rather than firing at them. Pure UI state, never stored. */
      consentSeen: false,
      overlapsBound: null
    };

    var store = config.testMode ? null : Core.safeStorage('localStorage');
    var saved = Core.readState(store);
    /* A reload picks the hand back up where it was: the session id is the only
       thing that has to survive, because the server is what remembers the
       cards. */
    game.sessionId = saved.sessionId || null;

    /* The hand on the table is canonical state, not a side effect of the DOM.
       The popup is rendered once per page and can be remounted at any time — a
       refresh, a back/forward, a section reload in the editor — and the shopper
       expects their hand back, not an empty table. So the visible hand is kept
       next to the reward state that already lives in localStorage.

       sessionStorage, deliberately: a hand is about THIS browsing session, not a
       durable fact about the shopper, and it is the same store the browser hands
       back after a reload. `testMode` gets no storage at all, exactly as the
       reward state does, so the theme-editor harness cannot inherit a hand. */
    var handStore = config.testMode ? null : Core.safeStorage('sessionStorage');
    var HAND_KEY = 'runit_blackjack_hand_v1';

    /* -------------------------------------------------------------- render */

    function setState(next) {
      game.state = next;
      root.setAttribute('data-state', next);
    }

    function setBlock(name, visible) {
      var node = blocks[name];
      if (!node) return;
      if (visible) {
        if (!node.hidden && node.classList.contains('is-in')) return;
        node.hidden = false;
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () {
            node.classList.add('is-in');
          });
        });
      } else {
        node.classList.remove('is-in');
        node.hidden = true;
      }
    }

    function setHeader(title, sub) {
      if (!el.title || !el.sub) return;
      el.title.classList.add('is-swapping');
      el.sub.classList.add('is-swapping');
      window.setTimeout(function () {
        el.title.textContent = title || el.title.textContent;
        el.sub.textContent = sub || '';
        el.title.classList.remove('is-swapping');
        el.sub.classList.remove('is-swapping');
      }, SPEED.settle);
    }

    /**
     * The line under the result — varied, and honest about the hand.
     *
     * The pool is chosen from `game.outcome`, so a bust reads as a bust and a
     * lost hand is never congratulated; the merchant's own line for that tier
     * leads the pool; and the line that was on screen for the PREVIOUS hand is
     * excluded, so two hands in a row never read the same. Kept on the game
     * object rather than in storage: what matters is one sitting.
     *
     * Falls back to the plain setting if an older copy of the core is in a
     * shopper's cache — a missing line is a smaller failure than a missing game.
     */
    function paintResultLine() {
      if (!el.resultKicker) return;
      var preferred = (config.kickers && config.kickers[game.tier]) || '';
      var line = preferred;
      if (typeof Core.resultLine === 'function') {
        line = Core.resultLine(game.outcome, game.lastResultLine || '', preferred);
      }
      game.lastResultLine = line || '';
      el.resultKicker.textContent = line || '';
    }

    function announce(text) {
      if (el.live) el.live.textContent = text;
    }

    function setMessage(node, text, isError) {
      if (!node) return;
      node.textContent = text || '';
      node.classList.toggle('is-error', Boolean(isError));
    }

    function setPill(node, text, variant) {
      if (!node) return;
      node.textContent = text;
      node.classList.remove('is-bust', 'is-blackjack');
      if (variant) node.classList.add(variant);
    }

    /* ----------------------------------------------------------- card DOM */

    function buildCard(card, faceDown) {
      var label = Core.accessibleCardLabel(card);
      var html = (faceDown ? cardBackHtml : cardFrontHtml)
        .split('%%RANK%%')
        .join(card.rank)
        .split('%%SUIT%%')
        .join(card.suit)
        .split('%%LABEL%%')
        .join(label);
      var host = document.createElement('div');
      host.innerHTML = html;
      return host.firstElementChild;
    }

    /**
     * Put a card on the table.
     *
     * `instant` is for a RESTORED card: it is not being dealt, so it arrives
     * already settled — no slide-in, no transform, no second deal animation when
     * the popup reopens onto a hand the shopper has already seen.
     */
    function appendCard(hand, card, faceDown, instant) {
      var node = buildCard(card, faceDown);
      hand.appendChild(node);
      if (instant) {
        node.style.transition = 'none';
        node.classList.add('is-dealt');
        window.requestAnimationFrame(function () {
          node.style.transition = '';
        });
      } else {
        window.requestAnimationFrame(function () {
          node.classList.add('is-dealt');
        });
      }
      return node;
    }


    function clearHands() {
      el.playerHand.innerHTML = '';
      el.dealerHand.innerHTML = '';
      game.dealerHiddenEl = null;
    }

    function syncTotals() {
      var player = Core.handTotal(game.player);
      var visible = Core.visibleTotal(game.dealer);
      var hidden = game.dealer.some(function (card) {
        return card.hidden;
      });

      var playerVariant = '';
      if (player.bust) playerVariant = 'is-bust';
      else if (Core.isBlackjack(game.player)) playerVariant = 'is-blackjack';

      setPill(el.playerTotal, String(player.total), playerVariant);

      if (hidden) {
        setPill(el.dealerTotal, visible.total + ' + ?', '');
      } else {
        var dealer = Core.handTotal(game.dealer);
        setPill(
          el.dealerTotal,
          String(dealer.total),
          dealer.bust ? 'is-bust' : Core.isBlackjack(game.dealer) ? 'is-blackjack' : ''
        );
      }
    }

    function unlockControls(unlocked) {
      el.hit.disabled = !unlocked;
      el.stand.disabled = !unlocked;
    }

    /** A finished hand never deals again: the controls stay inert, including
        after a reopen, so the result cannot be re-rolled. */
    function lockPlay() {
      unlockControls(false);
    }

    /** Put the keyboard on the close button once the panel has settled. */
    function focusClose() {
      if (!el.close) return;
      window.setTimeout(function () {
        try {
          el.close.focus({ preventScroll: true });
        } catch (e) {
          /* noop */
        }
      }, SPEED.reveal);
    }


    /* ------------------------------------------------------ hand persistence */

    /** A card as plain data: rank and suit identify it, `hidden` is real state. */
    function plainCard(card) {
      return { rank: card.rank, suit: card.suit, hidden: Boolean(card.hidden) };
    }

    /**
     * Write the hand down.
     *
     * Only what cannot be re-derived is stored: the cards, whether each is face
     * down, whether the hand is still being played, and the resolved outcome.
     * Totals, soft/hard, bust and blackjack all come back from the card list
     * through Core, so nothing derived is ever saved twice and nothing can drift.
     */
    function persistHand(phase) {
      if (!handStore) return;
      try {
        handStore.setItem(HAND_KEY, JSON.stringify(handData(phase)));
      } catch (e) {
        /* Quota or a locked-down session: the hand simply will not survive a
           remount. The game in front of the shopper is unaffected. */
      }
    }

    /** The snapshot itself. Shared with the durable copy kept beside a claim. */
    function handData(phase) {
      return {
        v: 1,
        /* The hand rides with the session that owns it, so a restore in a new
           tab can ask the worker about the same hand rather than guessing. */
        sessionId: game.sessionId || null,
        player: game.player.map(plainCard),
        dealer: game.dealer.map(plainCard),
        /* 'player' = still to be played, 'done' = the dealer is finished. */
        phase: phase || 'player',
        outcome: game.outcome || null,
        /* Carried so a replay finished in another tab — or after a reload — is
           still known to be a replay, and never falls back into the first-claim
           journey. */
        replay: Boolean(game.replayLive),
        at: now()
      };
    }

    /** Trust nothing off the wire, or out of storage: a bad snapshot is no
        snapshot, because half a hand is worse than a fresh one. */
    function sanitiseSnapshot(raw) {
      var data = raw;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (e) {
          return null;
        }
      }
      if (!data || data.v !== 1) return null;
      if (!Array.isArray(data.player) || !Array.isArray(data.dealer)) return null;

      var clean = function (card) {
        if (!card || typeof card.rank !== 'string' || typeof card.suit !== 'string') return null;
        return { rank: card.rank, suit: card.suit, hidden: Boolean(card.hidden) };
      };
      var player = data.player.map(clean);
      var dealer = data.dealer.map(clean);
      if (!player.length || player.indexOf(null) !== -1 || dealer.indexOf(null) !== -1) return null;

      return {
        player: player,
        dealer: dealer,
        phase: data.phase === 'player' ? 'player' : 'done',
        outcome: typeof data.outcome === 'string' ? data.outcome : null,
        replay: data.replay === true
      };
    }

    function readHand() {
      if (!handStore) return null;
      try {
        return handStore.getItem(HAND_KEY);
      } catch (e) {
        return null;
      }
    }

    /** A new hand is the ONLY thing that clears the saved one — never a close. */
    function dropHand() {
      if (!handStore) return;
      try {
        handStore.removeItem(HAND_KEY);
      } catch (e) {
        /* noop */
      }
    }

    /**
     * Deal the server's opening hand onto the table, one card at a time.
     *
     * The ORDER and the timing are the sequence this popup has always used
     * (player, dealer, player, dealer face down) — the only thing that changed
     * is where the cards came from. There is no deck in this file any more: the
     * shoe lives in the worker, and a hand dealt here could not be priced.
     */
    async function animateOpening(state) {
      var sequence = [
        { hand: 'player', index: 0 },
        { hand: 'dealer', index: 0 },
        { hand: 'player', index: 1 },
        { hand: 'dealer', index: 1 }
      ];

      for (var i = 0; i < sequence.length; i++) {
        var step = sequence[i];
        var card = state[step.hand][step.index];
        if (step.hand === 'player') {
          game.player.push({ rank: card.rank, suit: card.suit, hidden: false });
          appendCard(el.playerHand, card, false);
        } else if (card.hidden) {
          game.dealer.push({ rank: HOLE_CARD.rank, suit: HOLE_CARD.suit, hidden: true });
          game.dealerHiddenEl = appendCard(el.dealerHand, HOLE_CARD, true);
        } else {
          game.dealer.push({ rank: card.rank, suit: card.suit, hidden: false });
          appendCard(el.dealerHand, card, false);
        }
        syncTotals();
        await wait(SPEED.card * 0.62);
      }
    }

    /**
     * Repaint the table from a server hand with no motion.
     *
     * This is a RESTORE, not a deal: a reopen, a reconcile against another tab,
     * or the first paint of a hand that was already on the server. The table is
     * emptied first so a repaint can never double a card.
     */
    function paintServerHand(state) {
      clearHands();
      game.player = [];
      game.dealer = [];

      state.player.forEach(function (card) {
        var plain = { rank: card.rank, suit: card.suit, hidden: false };
        game.player.push(plain);
        appendCard(el.playerHand, plain, false, true);
      });
      state.dealer.forEach(function (card) {
        if (card.hidden) {
          var hole = { rank: HOLE_CARD.rank, suit: HOLE_CARD.suit, hidden: true };
          game.dealer.push(hole);
          game.dealerHiddenEl = appendCard(el.dealerHand, hole, true, true);
        } else {
          var plain = { rank: card.rank, suit: card.suit, hidden: false };
          game.dealer.push(plain);
          appendCard(el.dealerHand, plain, false, true);
        }
      });
      syncTotals();
    }

    /**
     * Does the table already show this hand?
     *
     * The hole card has no identity on the client while it is face down, so it
     * is compared by whether it is face down rather than by rank.
     */
    function tableMatches(state) {
      if (game.player.length !== state.player.length) return false;
      if (game.dealer.length !== state.dealer.length) return false;

      var playersMatch = state.player.every(function (card, i) {
        var mine = game.player[i];
        return Boolean(mine) && mine.rank === card.rank && mine.suit === card.suit;
      });
      if (!playersMatch) return false;

      return state.dealer.every(function (card, i) {
        var mine = game.dealer[i];
        if (!mine) return false;
        if (card.hidden) return mine.hidden === true;
        if (mine.hidden) return false;
        return mine.rank === card.rank && mine.suit === card.suit;
      });
    }

    /**
     * Repaint a saved hand onto an empty table.
     *
     * `data` is a snapshot — a raw one straight out of storage, or the copy that
     * travels with a claimed reward. Returns 'player', 'done' or null. Totals and
     * pills come from the restored card list, so the dealer's “11 + ?” returns
     * exactly as it was, and the table is emptied first so a repaint can never
     * double a card.
     */
    function renderSnapshot(data) {
      var snap = sanitiseSnapshot(data);
      if (!snap) return null;

      clearHands();
      game.player = snap.player;
      game.dealer = snap.dealer;
      game.outcome = snap.outcome;
      game.tier = snap.outcome ? Core.tierForResult(snap.outcome) : null;
      /* A hand still to be played that was dealt as a replay stays a replay: the
         finished-hand snapshots are not "live", so a reward screen restored
         from one never blocks the next replay. */
      game.replayLive = snap.replay === true && snap.phase === 'player';

      game.player.forEach(function (card) {
        appendCard(el.playerHand, card, false, true);
      });
      game.dealer.forEach(function (card) {
        var node = appendCard(el.dealerHand, card, card.hidden, true);
        if (card.hidden) game.dealerHiddenEl = node;
      });
      syncTotals();
      return snap.phase;
    }

    /** The hand from this browsing session, if there is one. */
    function restoreHand() {
      return renderSnapshot(readHand());
    }

    /* ----------------------------------------------------------- game flow */

    function resetForNewHand() {
      clearHands();
      /* A new hand gets a new session; whatever the last one was, it is not this
         table's any more. */
      game.sessionId = null;
      setPill(el.dealerTotal, '\u2013', '');
      setPill(el.playerTotal, '\u2013', '');
      setMessage(el.message, '', false);
      setMessage(el.smsMessage, '', false);
      if (el.form) el.form.classList.remove('has-error');
      if (el.mysteryCard) el.mysteryCard.classList.remove('is-flipped');
      if (el.codeActions) el.codeActions.hidden = false;
      setBlock('result', false);
      setBlock('email', false);
      setBlock('links', false);
      setBlock('code', false);
      setBlock('doubleDown', false);
      setBlock('mystery', false);
      setBlock('final', false);
      /* A hand in progress shows no "play again": the button belongs to the
         reward screens, and offering it mid-hand would be an invitation to
         abandon the hand on the table. */
      setPlayAgain(false);
      if (el.mysteryCard) el.mysteryCard.hidden = false;
      setHeader(config.intro.heading, config.intro.sub);
      announce('');
      game.outcome = null;
      game.tier = null;
      game.busy = false;
      unlockControls(false);
      if (el.scroll) el.scroll.scrollTop = 0;
    }

    /**
     * Start a hand.
     *
     * The cards are the WORKER's: this asks for a session and paints what comes
     * back. Nothing about the hand — the shoe, the deal, the totals, the outcome
     * — is decided in this file any more, because all of those were things a
     * shopper could edit in DevTools when they were.
     */
    async function deal() {
      if (game.busy) return;

      /* THE REWARD IS FINAL — one shopper, one result.

         Every caller comes through here, so this is the one place the rule has
         to hold: "Deal again", the inline surface's reset, the deferred deal on
         open, and anything a future change adds. A locked reward means the request
         to start a new hand is refused and the shopper is shown the result they
         already have, rather than being handed a free re-roll until the cards
         give them the top tier. */
      if (rewardLocked()) {
        track(EVENT_PREFIX + 'deal_blocked', { claimed: Boolean(game.code) });
        restoreLockedView();
        focusClose();
        return;
      }

      game.busy = true;
      /* The first-game journey is never a replay; make that explicit so a stale
         flag can never make this hand resolve as one. */
      game.replayLive = false;
      /* A new hand is the one thing that clears the saved one. Closing the popup
         deliberately does NOT: see the hand-persistence block above. */
      dropHand();
      resetForNewHand();
      game.busy = true;
      setState(STATES.DEALING);
      track(EVENT_PREFIX + 'started', { replayed: saved.plays > 0 });

      var started = await serverGame('start', {});
      if (!started.ok) {
        serverProblem(started);
        return;
      }

      var state = started.state;
      game.sessionId = state.sessionId;
      saved = Core.writeState(store, { sessionId: state.sessionId });
      game.player = [];
      game.dealer = [];

      await animateOpening(state);
      setState(STATES.PLAYER_TURN);
      persistHand('player');

      /* The server may have finished this hand already — a natural. Its result
         is the one that counts, so it is presented rather than re-derived. */
      if (state.status === 'resolved') {
        await wait(SPEED.step * 2);
        await settleFromServer(state);
        return;
      }

      game.busy = false;
      unlockControls(true);
      announce('Your total is ' + Core.handTotal(game.player).total + '. Hit or stand.');
      if (el.hit) el.hit.focus({ preventScroll: true });
    }

    /**
     * PLAY AGAIN — a fresh, server-authoritative hand, played for fun.
     *
     * The hand is dealt and scored exactly like the first one: this asks the
     * worker for a session marked as a replay and paints what comes back. What
     * is deliberately missing is the reward journey — no email form, no double
     * down, and no claim that could mint. When the hand resolves, the reward is
     * read back from the claim the shopper already holds.
     *
     * Two guards, and both are set synchronously before the first await, because
     * the interesting failure here is a double tap: `game.busy` covers an action
     * in flight and `game.replayLive` covers the whole hand, so no second replay
     * can start over the top of a live one.
     */
    async function beginReplay() {
      /* Two ways in, and neither can re-decide a reward: a shopper who has
         CLAIMED resolves to the reward they hold, and a shopper whose reward is
         decided but not yet claimed plays for fun and is handed back to the
         claim that is still theirs. A live first hand that is neither is
         refused — that, and only that, would be a re-roll. */
      if (!hasPermanentReward() && !rewardLocked()) return;
      if (game.busy || game.replayLive) return;

      game.replayLive = true;
      /* Freeze the reward the shopper already holds BEFORE the hand is dealt.
         Everything that shows a reward for a replay is read from here or from
         the server, so the hand's own percentage can never become the fallback.
         The saved RESULT outranks the live game for the same reason: it is the
         hand that earned the reward, and for an unclaimed reward it is the only
         copy of the percentage there is. */
      var earned = saved.result || {};
      game.replayClaim = {
        code: game.code || (saved.claimed && saved.claimed.code) || '',
        percent: Core.isAllowedPercent(earned.percent)
          ? earned.percent
          : Core.isAllowedPercent(game.percent)
            ? game.percent
            : saved.claimed && Core.isAllowedPercent(saved.claimed.percent)
              ? saved.claimed.percent
              : null,
        tier: earned.tier || game.tier || (saved.claimed && saved.claimed.tier) || null,
        email: (saved.claimed && saved.claimed.email) || ''
      };
      setPlayAgainBusy(true);

      dropHand();
      resetForNewHand();
      game.busy = true;
      setState(STATES.DEALING);
      track(EVENT_PREFIX + 'replay_started', {});

      var started = await serverGame('start', { replay: true });
      if (!started.ok) {
        /* The hand could not be dealt, so nothing was played and nothing is at
           stake: put the shopper back on the reward they already have, with the
           reason, and leave the button ready to try again. */
        game.replayLive = false;
        game.busy = false;
        var reason = (started && started.reason) || 'network';
        var message =
          reason !== 'network' && started && started.message
            ? started.message
            : config.claim.errors.network;
        track(EVENT_PREFIX + 'replay_unavailable', { reason: reason });
        restoreLockedView();
        setPlayAgain(true, message);
        setPlayAgainBusy(false);
        announce(message);
        return;
      }

      var state = started.state;
      game.sessionId = state.sessionId;
      game.player = [];
      game.dealer = [];

      await animateOpening(state);
      setState(STATES.PLAYER_TURN);
      persistHand('player');

      /* The server may have finished this hand already — a natural. Its result
         is the one that counts, so it is presented rather than re-derived. */
      if (state.status === 'resolved') {
        await wait(SPEED.step * 2);
        await settleFromServer(state);
        return;
      }

      game.busy = false;
      unlockControls(true);
      announce('Your total is ' + Core.handTotal(game.player).total + '. Hit or stand.');
      if (el.hit) el.hit.focus({ preventScroll: true });
    }

    async function hit() {
      if (game.busy || game.state !== STATES.PLAYER_TURN) return;
      game.busy = true;
      /* Controls go inert for the whole round trip: a second HIT before the
         first has answered would be a second card, and a HIT that overtook a
         STAND would be the shopper choosing which one landed. */
      unlockControls(false);

      var asked = await serverGame('hit', { sessionId: game.sessionId });
      if (!asked.ok) {
        serverProblem(asked);
        return;
      }

      var state = asked.state;
      var fresh = state.player.slice(game.player.length);
      for (var i = 0; i < fresh.length; i++) {
        game.player.push({ rank: fresh[i].rank, suit: fresh[i].suit, hidden: false });
        appendCard(el.playerHand, fresh[i], false);
        syncTotals();
        await wait(SPEED.card);
      }
      persistHand('player');

      var total = Core.handTotal(game.player);
      track(EVENT_PREFIX + 'hit', { hand_total: total.total });

      /* A bust, or a hand that reached 21, may be finished already: the server
         resolves the hand itself rather than waiting to be told. */
      if (state.status === 'resolved') {
        if (total.bust) announce('Bust with ' + total.total + '.');
        await settleFromServer(state);
        return;
      }

      game.busy = false;
      setState(STATES.PLAYER_TURN);
      unlockControls(true);
      announce('Your total is ' + total.total + '.');
    }

    async function stand() {
      if (game.busy || game.state !== STATES.PLAYER_TURN) return;
      game.busy = true;
      unlockControls(false);

      var asked = await serverGame('stand', { sessionId: game.sessionId });
      if (!asked.ok) {
        serverProblem(asked);
        return;
      }
      await settleFromServer(asked.state);
    }

    /**
     * Turn the face-down card over — into the card the SERVER says it is.
     *
     * The node was built as a back with a neutral face, because the worker does
     * not send the hole card's identity until the hand is over. So the flip
     * writes the real card in as it turns, rather than revealing something that
     * was sitting in the DOM (and in the response) all along.
     */
    function revealServerHole(card) {
      var node = game.dealerHiddenEl;
      if (!node) return false;

      var front = buildCard(card, false);
      node.innerHTML = front.innerHTML;
      node.setAttribute('data-rank', card.rank);
      node.setAttribute('data-suit', card.suit);
      node.setAttribute('aria-label', Core.accessibleCardLabel(card));
      node.classList.remove('rl-bj-card--face-down');

      game.dealerHiddenEl = null;
      return true;
    }

    /**
     * Play out a RESOLVED hand from the server's answer.
     *
     * The dealer's cards are already decided by the time this runs — this is the
     * animation of a finished hand, not a turn being taken. The hole card comes
     * over first, then each card the dealer drew, and the outcome that follows is
     * read off the response rather than recalculated from the table.
     */
    async function settleFromServer(state) {
      setState(STATES.DEALER_TURN);
      unlockControls(false);

      var serverDealer = state.dealer || [];

      /* The hole card, if the server has turned it over. */
      if (game.dealerHiddenEl && serverDealer.length >= 2 && serverDealer[1] && !serverDealer[1].hidden) {
        revealServerHole(serverDealer[1]);
        game.dealer[1] = { rank: serverDealer[1].rank, suit: serverDealer[1].suit, hidden: false };
        syncTotals();
        await wait(SPEED.flip);
        await wait(SPEED.step);
      }

      /* Then the cards the dealer drew, one at a time. */
      for (var i = game.dealer.length; i < serverDealer.length; i++) {
        game.dealer.push({ rank: serverDealer[i].rank, suit: serverDealer[i].suit, hidden: false });
        appendCard(el.dealerHand, serverDealer[i], false);
        syncTotals();
        await wait(SPEED.card);
      }

      game.player = state.player.map(plainCard);
      game.outcome = state.outcome;
      game.tier = state.tier;
      if (Core.isAllowedPercent(state.percent)) game.percent = state.percent;

      track(EVENT_PREFIX + 'stand', { reason: 'server', result: game.outcome });
      await resolve();
    }

    /**
     * The worker could not be reached, or would not play the hand.
     *
     * There is deliberately NO local fallback. Dealing a hand here would produce
     * cards the worker never saw, which either cannot be priced at all or — while
     * the legacy client-authoritative path still exists for the live theme —
     * could be claimed as whatever the browser said it was. A shopper who cannot
     * play honestly is offered a retry instead, which is what "Deal again" is at
     * this point in the flow.
     */
    function serverProblem(result) {
      game.busy = false;
      game.sessionId = null;
      unlockControls(false);
      lockPlay();
      setState(STATES.ERROR);

      var reason = (result && result.reason) || 'network';
      var message =
        (reason !== 'network' && result && result.message) || config.claim.errors.network;

      setMessage(el.message, message, true);
      setBlock('result', false);
      setBlock('email', false);
      setBlock('code', false);
      setBlock('doubleDown', false);
      setBlock('final', false);
      /* "Deal again" doubles as the retry, so it has to be visible — it is the
         only way back to a working hand without closing the popup. */
      if (el.dealAgain) {
        el.dealAgain.hidden = false;
        el.dealAgain.removeAttribute('aria-hidden');
      }
      setBlock('links', true);
      announce(message);
      track(EVENT_PREFIX + 'game_unavailable', { reason: reason });
      return null;
    }

    /**
     * Ask the worker what the hand actually IS.
     *
     * localStorage is a cache and is treated as one: it paints the table so a
     * reopen looks like a reopen, but the server is what remembers the cards. A
     * hand finished in another tab, or a session the worker has expired, is
     * exactly what this catches.
     *
     * Returns 'live' (the table now matches the server), 'gone' (the worker has
     * no such session, so a fresh hand is safe) or 'unknown' (a transient
     * failure — say nothing, deal nothing).
     */
    async function reconcileWithServer() {
      var sessionId = game.sessionId || saved.sessionId || '';
      if (!sessionId) return 'gone';

      var asked = await serverGame('state', { sessionId: sessionId });
      if (!asked.ok) {
        if (asked.reason === 'session_not_found') {
          game.sessionId = null;
          saved = Core.writeState(store, { sessionId: '' });
          dropHand();
          return 'gone';
        }
        return 'unknown';
      }

      var state = asked.state;
      game.sessionId = state.sessionId;
      saved = Core.writeState(store, { sessionId: state.sessionId });

      if (state.status === 'resolved') {
        paintServerHand(state);
        game.outcome = state.outcome;
        game.tier = state.tier;
        if (Core.isAllowedPercent(state.percent)) game.percent = state.percent;

        /* A hand dealt for FUN that finished elsewhere is still a replay, and it
           must not be written down as the shopper's result: that result — the
           one the reward came from — belongs to the FIRST hand. `game.replayLive`
           only survives a reopen while the hand is still being played, so the
           server's own flag is what decides here: it STAMPED the session as a
           replay when it dealt it, and a finished replay is exactly as much a
           replay as a live one. */
        if (game.replayLive || state.replay === true) {
          persistHand('done');
          await resolveReplay();
          return 'live';
        }

        /* Finished elsewhere. The result is the server's, and it is written down
           here so a reopen keeps showing it rather than starting a new hand. */
        saved = Core.writeState(store, {
          result: {
            outcome: game.outcome,
            tier: game.tier,
            percent: game.percent || Core.percentForTier(game.tier, config.rewards),
            /* Settled by the server, so the session that settled it is the hand
               that earned the reward: the one a later claim must name, however
               many hands are played for fun in between. See Core.claimSession. */
            sessionId: state.sessionId || game.sessionId || '',
            at: now()
          },
          hand: handData('done')
        });
        persistHand('done');
        if (saved.claimed && saved.claimed.code) restoreLockedView();
        else restoreResultStage();
        return 'live';
      }

      if (!tableMatches(state)) paintServerHand(state);
      game.busy = false;
      setState(STATES.PLAYER_TURN);
      unlockControls(true);
      announce('Your total is ' + Core.handTotal(game.player).total + '. Hit or stand.');
      return 'live';
    }

    async function resolve() {
      /* A hand that was played FOR FUN resolves against the reward the shopper
         already holds, never against this hand's tier. Everything below this
         line is the first-game journey — the email stage and the claim — and a
         replay must not enter it. */
      if (game.replayLive) return resolveReplay();

      /* The outcome and the tier are NOT decided here any more. They arrive in
         the response that finished the hand, from cards the worker dealt — which
         is what stops a shopper editing `game.outcome` in DevTools and claiming
         a blackjack they never drew. This function presents a result; it does not
         produce one. */
      /* The hand is finished and its result is known: from here on, reopening
         can paint the result stage straight from storage without re-resolving.
         Nothing else is written here, so a reopen cannot re-issue a code, send a
         second consent call or bump the played-hands count. */
      persistHand('done');

      var copy = config.results[game.outcome] || config.results.lose;
      setHeader(copy.heading, copy.sub);
      setState(STATES.RESULT);
      syncTotals();
      unlockControls(false);

      game.busy = true;

      paintResultLine();
      if (el.codeKicker) el.codeKicker.textContent = config.claim.codeKicker;

      setBlock('result', true);
      announce(copy.heading + ' ' + copy.sub);

      var eventName =
        game.outcome === 'blackjack' || game.outcome === 'both_blackjack'
          ? 'natural'
          : game.tier === 'win'
          ? 'win'
          : game.tier === 'push'
          ? 'push'
          : 'loss';
      track(EVENT_PREFIX + eventName, { result: game.outcome, tier: game.tier });

      saved = Core.writeState(store, {
        plays: Core.readState(store).plays + 1,
        /* THE REWARD IS FINAL, and this line is why.

           It is written the moment a hand RESOLVES, not when the address is
           submitted, because the exploit was never the claim: it was that
           "Deal again" ran the hand again, so a shopper could sit there until
           the cards gave them the best tier and only then hand over an address.
           From here on, this shopper's reward for the visit is this hand's, and
           `deal()` refuses to start another one.

           The finished hand rides along because a new tab has no sessionStorage
           to restore from, and the reward coming back over an empty table is
           the other half of this bug. */
        result: {
          outcome: game.outcome,
          tier: game.tier,
          /* The server's percentage when it sent one; the mirrored table is the
             fallback for a table restored from cache before any reply. */
          percent: game.percent || Core.percentForTier(game.tier, config.rewards),
          /* THE HAND THAT EARNED THIS. Written here, with the result, because
             this is the moment the reward is decided and the moment the shopper
             stops being able to play for it: every later hand is a replay, and a
             claim made after one of those has to name this session instead of
             the cards on the table. See Core.claimSession. */
          sessionId: game.sessionId || '',
          at: now()
        },
        hand: handData('done')
      });

      /* The reward is now final, so the offer to deal again goes with it. This
         has to happen HERE and not only where a code is rendered: the first
         hand ends on the email stage, which renders no code, so hiding it in
         `renderCode()` alone left the button on screen for exactly the shopper
         this rule exists to stop. */
      applyLockUi();

      game.busy = false;

      /* Already claimed once: the reward stays locked, so the hand is played
         for fun and the issued code is handed back rather than a second one. */
      if (saved.claimed && saved.claimed.code) {
        game.code = saved.claimed.code;
        game.percent = saved.claimed.percent;
        game.tier = saved.claimed.tier || game.tier;
        await wait(SPEED.reveal);
        renderCode(saved.claimed.percent, saved.claimed.code, true);
        setHeader(
          config.final.heading,
          fmt(config.final.sub, { percent: saved.claimed.percent, code: saved.claimed.code })
        );
        setBlock('code', true);
        setBlock('links', true);
        setState(STATES.REWARD_REVEAL);
        alignFlowTop();
        return;
      }

      await wait(SPEED.reveal);
      renderRewardCopy();
      setBlock('email', true);
      setState(STATES.EMAIL_CAPTURE);
      await wait(SPEED.reveal);
      setBlock('links', true);
    }

    /* ----------------------------------------------------------- replay end */

    /**
     * The end of a hand played for fun.
     *
     * The hand itself is the server's, like any other, but the REWARD is not
     * this hand's to give. It is read back from the claim the shopper already
     * holds, so a replay that draws a natural shows exactly the same code and
     * percentage as one that busts. There is no email stage here and no double
     * down: those belong to the first claim, and repeating them is precisely
     * what playing again must not do.
     */
    async function resolveReplay() {
      persistHand('done');

      var copy = config.results[game.outcome] || config.results.lose;
      setHeader(copy.heading, copy.sub);
      setState(STATES.RESULT);
      syncTotals();
      unlockControls(false);

      paintResultLine();
      setBlock('result', true);
      setBlock('email', false);
      setBlock('doubleDown', false);
      announce(copy.heading + ' ' + copy.sub);
      track(EVENT_PREFIX + 'replay_result', { result: game.outcome, tier: game.tier });

      var reward = await resolvePermanentReward();
      game.code = reward.code;
      game.percent = reward.percent;
      if (reward.tier) game.tier = reward.tier;

      game.replayLive = false;
      game.replayClaim = null;
      game.busy = false;
      setPlayAgainBusy(false);

      /* NO CODE ARRIVED, because there is not one yet to arrive: the shopper's
         reward is decided but unclaimed, so this fun hand has nothing to pay
         out and must not be dressed as if it had. They are put back on the
         claim their FIRST hand earned — "Deal again" and "Nah, I'm good" still
         underneath it — and the claim is priced from that hand's session
         (Core.claimSession), never from a hand played for fun. */
      if (!Core.isValidCode(reward.code)) {
        reopenClaimStage();
        return null;
      }

      renderCode(reward.percent, reward.code, true);
      setBlock('code', true);
      /* The row comes back with the reward it belongs to: the big button is the
         way into another fun hand, and "Nah, I'm good" is how the shopper leaves
         the table from here — the same two ways out as every other reward
         screen, minus the duplicate.

         Shown BEFORE the button is offered, because the offer scrolls itself
         into view and the row it sits above has to exist when that measurement
         is taken, or the button lands below the fold by exactly this row's
         height. */
      setBlock('links', true);
      /* The one line that says it plainly: the hand is new, the reward is not. */
      setPlayAgain(
        true,
        fmt((config.playAgain && config.playAgain.result) || '', { percent: reward.percent })
      );
      setState(STATES.REWARD_REVEAL);
      alignFlowTop();
      return null;
    }

    /**
     * Give the shopper back the claim a hand played for fun interrupted.
     *
     * The percentage comes off the SAVED result — the hand that earned it — and
     * deliberately not off the cards on the table, which were dealt for fun and
     * are worth nothing to anybody. Everything else is the claim stage as it
     * was left: the reward line, the address form, and the two links, so
     * "Deal again" is offered exactly where the shopper found it.
     */
    function reopenClaimStage() {
      var earned = saved.result || {};
      var percent = Core.isAllowedPercent(earned.percent) ? earned.percent : game.percent;
      if (Core.isAllowedPercent(percent)) game.percent = percent;
      game.tier = earned.tier || game.tier;
      renderRewardCopy();
      setBlock('email', true);
      setBlock('links', true);
      /* The big play-again button belongs to a reward the shopper HOLDS, and
         this one is still theirs to claim: the row is the way back in. */
      setPlayAgain(false);
      applyLockUi();
      setState(STATES.EMAIL_CAPTURE);
      alignFlowTop();
    }

    /**
     * The reward the shopper already owns, resolved the authoritative way.
     *
     * `replay: true` asks the worker for the EXISTING claim: the server looks it
     * up and will not mint another one, whatever this hand turned out to be. The
     * stored claim is the fallback for the case where the worker cannot be
     * reached — it is the same reward, and showing it costs nothing, because a
     * screen is not what makes a discount.
     */
    async function resolvePermanentReward() {
      /* The frozen copy is preferred over storage because it is the reward as it
         stood when the button was pressed; `game.percent` is deliberately NOT a
         fallback, because at this point it holds the REPLAY hand's number, and
         the promise of this screen is that the replay's number never reaches
         the shopper. */
      var cached = game.replayClaim || saved.claimed || null;
      var email = (cached && cached.email) || (saved.claimed && saved.claimed.email) || '';
      var asked = email ? await requestReplayReward(email) : null;
      /* The server's answer wins; the frozen reward is the fallback. */
      var reward = Core.replayReward(asked, cached, config.rewards);
      if (reward) return reward;

      var tier = (cached && cached.tier) || null;
      return {
        code: (cached && cached.code) || '',
        percent: tier ? Core.percentForTier(tier, config.rewards) : null,
        tier: tier
      };
    }

    function renderRewardCopy() {
      /* The percentage the SERVER priced this hand at. The local table mirrors
         it for the editor preview, but it never overrides a reply. */
      if (!Core.isAllowedPercent(game.percent)) {
        game.percent = clampPercent(Core.percentForResult(game.outcome, config.rewards), 10);
      }
      var vars = { percent: game.percent, code: '' };
      if (el.reward) el.reward.textContent = fmt(config.claim.rewardTemplate, vars) + minimumNote(vars.percent, ' OVER £');
      if (el.claimLabel) el.claimLabel.textContent = fmt(config.claim.ctaTemplate, vars);
      if (el.email) el.email.setAttribute('aria-label', config.claim.placeholder);
    }

    /* --------------------------------------------------------- reward flow */

    /**
     * ONE request does the whole claim in proxy mode: the address becomes a real
     * Shopify marketing subscriber AND the discount code is allocated, both
     * server-side behind Shopify's app proxy.
     *
     * The proxy verifies Shopify's own signature over the request, validates and
     * rate-limits the address, creates or UPDATES the customer with
     * email-marketing consent through the Admin API, prices the hand from its own
     * table and returns a real code. Nothing secret is ever in this file.
     *
     * Why not the storefront's own customer form, which is the obvious answer?
     * Because it cannot be driven from a popup without offering a challenge the
     * popup has no way to finish. Measured on this store: Shopify protects
     * `form_type=customer` posts to /contact with its storefront-forms hCaptcha,
     * the provider only ever writes `h-captcha-response` during ITS OWN submit,
     * and `Shopify.captcha.protect()` resolves without writing a token at all — so
     * the only thing left for Shopify to do is raise an interactive challenge, and
     * the token-less fetch behind it comes back `Invalid CAPTCHA token`. That is a
     * dead end inside a popup, so consent is recorded server-side instead, through
     * the same signed proxy the code comes from.
     *
     * hCaptcha is untouched where it belongs: every Shopify form on the store —
     * contact, newsletter, the footer signup, the email-signup banner — is still
     * protected, and the storefront-forms provider still loads for them. The popup
     * simply no longer posts to a Shopify form, so it never asks for a widget it
     * could not use. See docs/finalisation/48-* and 56-*.
     */
    /* ------------------------------------------------------ marketing consent */

    /**
     * Record the shopper's address — and their number, when the double-down stage
     * collected one — as a real Shopify marketing subscriber.
     *
     * One signed POST to the rewards worker, which holds the Admin API credentials
     * and writes the consent itself. There is nothing for the shopper to solve
     * here, because there is no Shopify form: the request is signature-checked and
     * rate-limited at the other end, which is exactly what hCaptcha was protecting
     * on the form we no longer use.
     *
     * Never fatal for the email step: in direct mode the reward is a shared code
     * the theme already holds, so the game pays out whether or not our backend
     * answers. The double-down step is the exception — it must never tell a shopper
     * their number was saved when it was not.
     */
    /* Which consent endpoint answered last. Module-scoped per game instance so
       a page that renders the popup twice does not share the memory. */
    var consentRoute = null;

    /* Which CLAIM endpoint answered last, for the same reason: the app proxy is
       the intended route and a store whose proxy does not exist yet should pay
       one wasted request per page load, not one per claim. */
    var claimRoute = null;

    /** Candidate URLs, in order, de-duplicated, with the one that already
        answered promoted to the front. */
    function uniqueUrls(candidates, preferred) {
      var seen = {};
      var urls = [];
      for (var i = 0; i < candidates.length; i++) {
        var url = typeof candidates[i] === 'string' ? candidates[i].trim() : '';
        if (!url || seen[url]) continue;
        seen[url] = true;
        urls.push(url);
      }
      if (preferred) {
        var at = urls.indexOf(preferred);
        if (at > 0) {
          urls.splice(at, 1);
          urls.unshift(preferred);
        }
      }
      return urls;
    }

    /**
     * The consent endpoints, in the order they are tried.
     *
     * The app proxy is the intended route: Shopify signs the request for us. It
     * is a Shopify-side setting, though, and until it exists `/apps/…` answers
     * 404 from Shopify itself — which is the failure the double-down stage was
     * reporting as "that number is wrong". `claim.subscribeFallback` is the
     * rewards worker's own hostname, which is reachable directly, so consent does
     * not depend on a dashboard setting being in place. Whichever answers first
     * is remembered, so a missing proxy costs one extra request per page load
     * rather than one per submission.
     */
    function consentEndpoints() {
      return uniqueUrls([config.claim.subscribe, config.claim.subscribeFallback], consentRoute);
    }

    /**
     * The claim endpoints, in the order they are tried.
     *
     * Exactly the same bargain as the consent route, and it has to be, because
     * the reward itself is what the proxy carries: the live app proxy is
     * `/apps/runit/blackjack/claim`, and the popup's own fallback setting is
     * empty on
     * purpose — the backend's hostname is not meant to appear anywhere a shopper
     * can read it. The list exists so a store whose proxy is missing still has
     * one candidate to try rather than none.
     */
    function claimEndpoints() {
      return uniqueUrls([config.claim.url, config.claim.claimFallback], claimRoute);
    }

    /* Which GAME endpoint answered last, for the same reason: one wasted request
       per page load on a store whose app proxy does not exist, not one per card. */
    var gameRoute = null;

    /**
     * The game endpoints, in the order they are tried.
     *
     * Derived from the claim endpoints on purpose. `/…/claim` and `/…/game/start`
     * are the same worker on the same transport, so a store that has an app proxy
     * reaches both through it and a store that does not reaches both directly —
     * and there is no second setting to drift out of step with the first.
     */
    function gameEndpoints(action) {
      var sources = [config.claim.url, config.claim.claimFallback];
      var candidates = [];
      for (var i = 0; i < sources.length; i++) {
        var url = typeof sources[i] === 'string' ? sources[i].trim() : '';
        if (!url) continue;
        candidates.push(url.replace(/\/claim\/?$/, '/game/' + action));
      }
      return uniqueUrls(candidates, gameRoute);
    }

    /**
     * Trust nothing off the wire.
     *
     * A hand is only usable when every card it names has a rank and a suit the
     * renderer knows and the status is one the UI understands. Half a hand is
     * worse than none: it would leave the shopper hitting cards nobody can price.
     */
    function readGameState(result) {
      var data = result.data || {};
      if (!result.ok || data.success !== true) return null;
      if (typeof data.sessionId !== 'string' || !data.sessionId) return null;
      if (data.status !== 'playing' && data.status !== 'resolved') return null;

      var clean = function (card) {
        if (!card || typeof card !== 'object') return null;
        /* The hole card arrives as `{ hidden: true }` and nothing else — the
           worker withholds its identity until the hand is over. */
        if (card.hidden === true) return { hidden: true };
        if (typeof card.rank !== 'string' || typeof card.suit !== 'string') return null;
        if (Core.RANKS.indexOf(card.rank) === -1 || Core.SUITS.indexOf(card.suit) === -1) return null;
        return { rank: card.rank, suit: card.suit, hidden: false };
      };

      var player = (data.player || []).map(clean);
      var dealer = (data.dealer || []).map(clean);
      if (player.length < 2 || dealer.length < 2) return null;
      if (player.indexOf(null) !== -1 || dealer.indexOf(null) !== -1) return null;

      return {
        sessionId: data.sessionId,
        status: data.status,
        /* The server says which kind of hand this is; the client only ever
           confirms what it asked for. */
        replay: data.replay === true,
        player: player,
        dealer: dealer,
        outcome: typeof data.outcome === 'string' ? data.outcome : null,
        tier: typeof data.tier === 'string' ? data.tier : null,
        percent: Core.isAllowedPercent(data.discountPercent) ? data.discountPercent : null
      };
    }

    /**
     * One game action, answered by the worker.
     *
     * This function's whole job is transport: ask, try the other transport when
     * the route is missing, and hand back the server's answer or a reason it is
     * unusable. It NEVER invents a hand — see `serverProblem()` for why.
     */
    function serverGame(action, payload) {
      var urls = gameEndpoints(action);
      if (!urls.length) return Promise.resolve({ ok: false, reason: 'unconfigured', message: '' });

      var attempt = function (index) {
        return postJson(urls[index], payload || {})
          .catch(function () {
            return { ok: false, status: 0, data: null };
          })
          .then(function (result) {
            var data = result.data || {};

            /* "That route is not here" is the only answer that earns a retry on
               the other endpoint. A 4xx/5xx the worker itself produced is an
               answer about the REQUEST, and replaying it would spend another
               rate-limited write. */
            if (routeMissing(result) && index + 1 < urls.length) {
              track(EVENT_PREFIX + 'game_route_missing', { status: result.status, action: action });
              return attempt(index + 1);
            }
            gameRoute = urls[index];

            var state = readGameState(result);
            if (!state) {
              return {
                ok: false,
                reason: (data && data.error) || (result.ok ? 'rejected' : 'network'),
                message: data && typeof data.message === 'string' ? data.message : '',
                status: result.status
              };
            }
            return { ok: true, state: state };
          });
      };

      return attempt(0);
    }

    /* "That route is not here" — the only answer that means the next endpoint is
       worth trying. A 4xx/5xx the worker itself produced is an answer about the
       REQUEST, and replaying it elsewhere would double a rate-limited write. */
    function routeMissing(result) {
      return !result.ok && (result.status === 0 || result.status === 404 || result.status === 405);
    }

    function readConsent(result) {
      var data = result.data || {};
      var ok = Boolean(result.ok && data.success === true);
      return {
        ok: ok,
        status: result.status,
        subscribed: Boolean(data.subscribed),
        already: Boolean(data.already),
        /* null means "not applicable" (no number was sent); false means "we tried
           and it did not store". The double-down stage must not treat those the
           same. */
        phone: data.phone === null || data.phone === undefined ? null : Boolean(data.phone),
        sms: data.sms === null || data.sms === undefined ? null : Boolean(data.sms),
        /* Why a number did not store, when the worker can say: `phone_in_use`
           means Shopify has it on another customer — a dead end, not a glitch —
           and the stage words it differently from a transient failure. */
        phoneError: typeof data.phoneError === 'string' ? data.phoneError : '',
        reason: ok ? '' : result.status === 0 ? 'network' : data.error || 'rejected',
        message: typeof data.message === 'string' ? data.message : ''
      };
    }

    function postConsent(url, payload) {
      return postJson(url, payload).catch(function () {
        return { ok: false, status: 0, data: null };
      });
    }

    function tryConsent(urls, index, payload) {
      return postConsent(urls[index], payload).then(function (result) {
        if (routeMissing(result) && index + 1 < urls.length) {
          track(EVENT_PREFIX + 'consent_route_missing', { status: result.status, index: index });
          return tryConsent(urls, index + 1, payload);
        }
        consentRoute = urls[index];
        return readConsent(result);
      });
    }

    function subscribeRemote(details) {
      var urls = consentEndpoints();
      if (!urls.length) return Promise.resolve({ ok: false, status: 0, reason: 'unconfigured', message: '' });

      return tryConsent(urls, 0, {
        email: (details && details.email) || '',
        phone: (details && details.phone) || '',
        phoneE164: (details && details.phoneE164) || ''
      });
    }

    /**
     * One claim POST, with the route fallback both claim paths share.
     *
     * The first claim and a replay claim travel the same way; they differ only
     * in what they ask for. Keeping the transport in one place means the retry
     * rules ("a 404 means try the other endpoint; a 429 does not") and the
     * response validation cannot drift between them.
     */
    function postClaim(urls, payload) {
      var attempt = function (index) {
        return postJson(urls[index], payload)
          .catch(function () {
            return { ok: false, status: 0, data: null };
          })
          .then(function (result) {
            var data = result.data || {};

            /* "That route is not here" is the only answer that earns a retry on
               the other endpoint. A 4xx/5xx the worker itself produced is an
               answer about the REQUEST, and replaying it would only spend
               another rate-limited write. */
            if (routeMissing(result) && index + 1 < urls.length) {
              track(EVENT_PREFIX + 'claim_route_missing', {
                status: result.status,
                index: index
              });
              return attempt(index + 1);
            }
            claimRoute = urls[index];

            if (!result.ok || data.success !== true) {
              /* The server answers with a sentence a shopper can act on; use
                 theirs rather than inventing a second one here. */
              return {
                ok: false,
                reason: data.error || (result.ok ? 'claim' : 'network'),
                message: typeof data.message === 'string' ? data.message : '',
                status: result.status
              };
            }

            if (!Core.isValidCode(data.code)) {
              /* A malformed code is worse than no code — it will not redeem. */
              return { ok: false, reason: 'claim' };
            }

            var percent = Core.isAllowedPercent(data.discountPercent)
              ? data.discountPercent
              : clampPercent(data.discountPercent, game.percent);

            return {
              ok: true,
              code: data.code,
              percent: percent,
              duplicate: Boolean(data.duplicate),
              /* Whether the server resolved this to a reward the shopper already
                 held. A replay always is one. */
              rewardLocked: Boolean(data.rewardLocked),
              /* The server checked the shopper's own code: already spent means
                 the screen offers referrals instead of a dead code. */
              rewardUsed: data.rewardUsed === true,
              replay: Boolean(data.replay),
              /* The tier the SERVER recorded for this claim. On a repeat that is
                 the reward already on the shopper's customer, which may not be
                 the tier of the hand on the table — and the screen has to show
                 the one they actually hold. */
              tier: typeof data.tier === 'string' ? data.tier : null,
              /* The consent is a separate errand from the reward: it can fail
                 and be retried server-side without ever costing the shopper
                 their code. */
              subscribed: data.subscribed !== false
            };
          });
      };

      return attempt(0);
    }

    function requestCode(email) {
      if (config.claim.mode === 'direct') {
        var direct = (config.claim.codes || {})[game.tier];
        if (!direct) return Promise.resolve({ ok: false, reason: 'unconfigured' });
        /* The code is shared and needs no server, so it must NOT wait on one.
           The consent call is a separate errand that can fail and be retried
           server-side; blocking the reward on it would make a shopper stare at a
           spinner for a discount we already hold. Reveal now; the consent settles
           behind it, and failing is only ever reported. */
        subscribeRemote({ email: email }).then(function (sub) {
          if (!sub.ok) track(EVENT_PREFIX + 'consent_failed', { tier: game.tier, reason: sub.reason });
        });
        return Promise.resolve({
          ok: true,
          code: String(direct),
          percent: game.percent,
          subscribed: true
        });
      }

      var urls = claimEndpoints();
      if (!urls.length) return Promise.resolve({ ok: false, reason: 'unconfigured' });

      /* The hand that EARNED the reward, which is not always the hand on the
         table: a shopper may have played for fun before typing an address, and
         this hand's session is the one the worker is allowed to price. */
      var sessionId = Core.claimSession(saved) || game.sessionId || saved.sessionId || '';
      if (!sessionId) {
        /* No session means no hand the worker can price. Posting `gameResult`
           instead is exactly the hole this replaced — the browser deciding its
           own reward — so a claim without a session is refused here rather than
           falling back to it. */
        return Promise.resolve({ ok: false, reason: 'no_session' });
      }

      var payload = {
        email: email,
        /* The hand's proof, and the ONLY thing that decides the reward: the
           worker reads the tier off this session. `gameResult`/`tier` are
           deliberately no longer sent — the outcome is not ours to state, and
           sending it would change nothing. */
        sessionId: sessionId,
        shop: window.Shopify && window.Shopify.shop ? window.Shopify.shop : undefined
      };

      return postClaim(urls, payload);
    }

    /**
     * Ask the worker for the reward the shopper already holds.
     *
     * A replay claim — and a read in everything but name: the server resolves it
     * to the permanent claim and refuses to mint. Returns null when it has
     * nothing usable to say, so the caller can fall back to the stored reward.
     */
    function requestReplayReward(email) {
      var urls = claimEndpoints();
      if (!urls.length || !email) return Promise.resolve(null);

      return postClaim(urls, {
        email: email,
        sessionId: game.sessionId || '',
        /* Entertainment only. The session this names was dealt as a replay, so
           the server holds even if this field is left out. */
        replay: true
      }).then(function (issued) {
        return issued && issued.ok
          ? { code: issued.code, percent: issued.percent, tier: issued.tier }
          : null;
      });
    }

    async function claim() {
      if (game.busy) return;
      var email = el.email ? el.email.value.trim() : '';

      if (!email) {
        return failEmail(config.claim.errors.email);
      }
      if (!EMAIL_RE.test(email) || email.length > 254) {
        return failEmail(config.claim.errors.email);
      }

      setMessage(el.message, '', false);
      if (el.form) el.form.classList.remove('has-error');
      setBusy(el.claim, true, config.claim.pending);
      setState(STATES.CLAIMING);
      track(EVENT_PREFIX + 'email_submit', { result: game.outcome, tier: game.tier });

      var issued = await requestCode(email);
      if (!issued.ok) {
        setBusy(el.claim, false, null);

        /* The address itself was refused: keep the shopper on the form and put
           the cursor back in it, exactly as a local validation failure does. */
        if (issued.reason === 'invalid_email' || issued.reason === 'unsupported_result') {
          setState(STATES.EMAIL_CAPTURE);
          return failEmail(issued.message || config.claim.errors.email);
        }

        setState(STATES.ERROR);
        /* The proxy sends a shopper-readable sentence with every failure, so the
           theme copy is the fallback rather than the other way round. */
        var fallback =
          issued.reason === 'allocation_unavailable' || issued.reason === 'claim'
            ? config.claim.errors.claim
            : config.claim.errors.network;
        setMessage(el.message, issued.message || fallback, true);
        return;
      }

      /* Reported, never shown to the shopper: their reward is not contingent on
         our Admin API call, but the merchant needs to know it failed. */
      if (!issued.subscribed) {
        track(EVENT_PREFIX + 'consent_failed', { tier: game.tier });
      }

      /* AN EXISTING REWARD OUTRANKS THE HAND JUST PLAYED.

         This shopper has claimed before, so the hand on the table is not what
         they are being paid for — `requestCode` handed back the reward held on
         their Shopify customer instead. The screen has to say so. Leaving the
         hand's own headline above a code it did not win read as "DEALER BUSTS"
         sitting over a 10% reward, which is the one thing worse than either
         half: it told the shopper they had both lost and won.

         Hiding the result stage and letting the reward heading carry the screen
         is exactly what reopening the popup already does (see
         `restoreLockedView`), so the replay looks the same however the shopper
         arrives at it. The tier comes from the server, so "One reward per
         shopper" sits under the reward they actually hold. */
      var repeat = issued.duplicate === true;
      game.code = issued.code;
      game.percent = issued.percent;
      if (repeat && issued.tier) game.tier = issued.tier;

      saved = Core.writeState(store, {
        claimed: {
          result: game.outcome,
          tier: game.tier,
          percent: issued.percent,
          code: issued.code,
          email: email,
          at: now(),
          /* Whether this was the reward they already had rather than one won
             in the hand on the table. */
          repeat: repeat,
          /* The hand rides with the reward: this is the table the code was won
             on, and the shopper will come back to it in a new tab, not just in
             this one. Reconstructed on open, never re-resolved. */
          hand: handData('done')
        }
      });

      setBusy(el.claim, false, null);
      setBlock('email', false);
      if (repeat) {
        setBlock('result', false);
        setHeader(
          config.final.heading,
          fmt(config.final.sub, { percent: issued.percent, code: issued.code })
        );
      }
      renderCode(issued.percent, issued.code, repeat);
      setState(STATES.REWARD_REVEAL);
      setBlock('code', true);
      if (repeat && issued.rewardUsed) {
        showReferral();
        return;
      }
      announce('Your code is ' + issued.code);
      /* The reward is dealt underneath a table that never moves. On desktop the
         whole reward fits the panel if it starts at the top of it, and on a
         phone this is simply the shortest scroll to the payoff. */
      alignFlowTop();
      track(EVENT_PREFIX + 'reward_claimed', {
        result: game.outcome,
        tier: game.tier,
        percent: issued.percent
      });

      await maybeAdvanceToDoubleDown();
    }

    function failEmail(text) {
      if (el.form) el.form.classList.add('has-error');
      setMessage(el.message, text, true);
      if (el.email) {
        el.email.focus({ preventScroll: true });
        el.email.setAttribute('aria-invalid', 'true');
      }
      return null;
    }

    function setBusy(button, busy, label) {
      if (!button) return;
      button.setAttribute('aria-busy', busy ? 'true' : 'false');
      button.disabled = Boolean(busy);
      if (busy && label && el.claimLabel) el.claimLabel.textContent = label;
    }

    function renderCode(percent, code, alreadyClaimed) {
      applyLockUi();
      /* The reward exists, so the shopper may play on for fun. This is the one
         function every reward screen goes through — the first claim, a repeat
         claim, a replay and a restore alike — so the offer is made here rather
         than at each call site. */
      setPlayAgain(true);
      var vars = { percent: percent, code: code };
      var meta = fmt(config.claim.codeMeta, vars) + minimumNote(percent, ' orders £', '+');

      if (el.codeValue) el.codeValue.textContent = code;
      if (el.codeMeta) el.codeMeta.textContent = meta;
      if (el.codeNote) {
        el.codeNote.textContent = alreadyClaimed
          ? config.claim.codeNote + ' One reward per shopper.'
          : config.claim.codeNote;
      }
      if (el.finalCode) el.finalCode.textContent = code;
      if (el.finalMeta) el.finalMeta.textContent = meta;
      if (el.finalSub) el.finalSub.textContent = fmt(config.final.sub, vars);
      if (el.reward) el.reward.textContent = fmt(config.claim.rewardTemplate, vars) + minimumNote(vars.percent, ' OVER £');
      if (el.claimLabel) el.claimLabel.textContent = fmt(config.claim.ctaTemplate, vars);
      if (el.ddLocked) el.ddLocked.textContent = fmt(config.doubleDown.locked, vars);
    }

    async function maybeAdvanceToDoubleDown() {
      if (!config.doubleDown || !config.doubleDown.enabled) {
        return showFinal();
      }
      if (config.doubleDown.auto) {
        await wait(SPEED.autoAdvance);
        if (game.state !== STATES.REWARD_REVEAL) return;
        /* No focus steal here: the code is what the shopper is reading, the
           phone stage simply arrives underneath it. */
        return openDoubleDown(false);
      }
      /* Manual: offer the second stage without forcing it. */
      if (el.ddStart) el.ddStart.hidden = false;
      setState(STATES.REWARD_REVEAL);
      return null;
    }

    /**
     * The double down belongs to the same screen as the code: the code stays
     * above it and the popup's own headline keeps the hand result, so nothing
     * the shopper has already read disappears when the phone stage lands.
     */
    function openDoubleDown(focusPhone) {
      setState(STATES.DOUBLE_DOWN);
      if (el.ddStart) el.ddStart.hidden = true;
      if (el.ddLocked) {
        el.ddLocked.textContent = fmt(config.doubleDown.locked, {
          percent: game.percent,
          code: game.code
        });
      }
      if (el.mysteryCard) el.mysteryCard.hidden = false;
      /* Two ways to pay with the same card: the shop CTA would sit between the
         code and the phone number, so it steps aside until the final screen. */
      if (el.codeActions) el.codeActions.hidden = true;
      setBlock('doubleDown', true);
      track(EVENT_PREFIX + 'double_down_view', { percent: game.percent });
      alignFlowTop();
      if (focusPhone && el.phone) {
        el.phone.focus({ preventScroll: true });
        scrollIntoView(el.phone);
      }
    }

    function openFinal() {
      return showFinal();
    }

    /** Keep a newly revealed block visible without jumping the whole page. */
    function scrollIntoView(node) {
      if (!node || !el.scroll || inline) return;
      var container = el.scroll;
      var top = node.getBoundingClientRect().top - container.getBoundingClientRect().top;
      var target = container.scrollTop + top - 16;
      if (target <= container.scrollTop + 4) return;
      container.scrollTo({ top: target, behavior: REDUCED ? 'auto' : 'smooth' });
    }

    /**
     * Put the start of the reward flow at the top of the panel.
     *
     * The table never leaves the DOM, so on a desktop window the reward stages
     * are taller than what is left below it. Aligning the flow means the
     * shopper reads the reward without scrolling, and the finished hand stays
     * one flick up. A no-op on the inline page surface, which owns its scroll.
     */
    function alignFlowTop() {
      if (!el.scroll || !el.flow || inline) return;
      var container = el.scroll;
      var top = el.flow.getBoundingClientRect().top - container.getBoundingClientRect().top;
      var target = Math.max(0, container.scrollTop + top - 12);
      if (target <= container.scrollTop + 2) return;
      container.scrollTo({ top: target, behavior: REDUCED ? 'auto' : 'smooth' });
    }

    /** Terminal state: the reward is kept, the code is shown once more. */
    function showFinal() {
      setState(STATES.FINAL);
      renderCode(game.percent, game.code, false);
      setBlock('doubleDown', false);
      setBlock('code', false);
      setBlock('final', true);
      setBlock('links', true);
      if (el.ddStart) el.ddStart.hidden = true;
      setHeader(config.final.heading, fmt(config.final.sub, { percent: game.percent, code: game.code }));
      /* Keep the code and the shop CTA on screen together. */
      alignFlowTop();
    }

    /* ------------------------------------------------------------ sms flow */

    /* The three things that can go wrong with a number are not the same thing,
       and only one of them is the shopper's to fix. */
    function invalidNumberMessage() {
      return config.doubleDown.errorInvalid || config.doubleDown.error;
    }

    function unavailableNumberMessage() {
      return config.doubleDown.errorUnavailable || config.doubleDown.error;
    }

    /* A number that belongs to somebody else's account. Distinct from a
       transient failure because retrying will never fix it, and it must not
       hint at whose account holds the number. */
    function inUseNumberMessage() {
      return config.doubleDown.errorInUse || '';
    }

    /**
     * Turn a failed consent answer into something the shopper can act on.
     *
     * The worker's own sentence wins when it has one — it knows whether this was
     * a rate limit, a refusal, or a number Shopify would not store — and the
     * fallbacks below only cover the case where no endpoint answered at all
     * (a 404 from Shopify, a dead route, a dropped connection), which used to be
     * reported as "check your number".
     */
    function consentFailureMessage(result) {
      if (result && result.message) return result.message;
      if (result && (result.reason === 'network' || result.reason === 'unconfigured' || routeMissing(result))) {
        return unavailableNumberMessage();
      }
      return config.doubleDown.error;
    }

    function markSmsError(message) {
      setState(STATES.ERROR);
      if (el.smsForm) el.smsForm.classList.add('has-error');
      setMessage(el.smsMessage, message, true);
    }

    async function submitSms(event) {
      if (event) event.preventDefault();
      if (game.busy) return;

      var direct = config.claim.mode === 'direct';
      /* The number belongs on the CONSENT call whichever mode the reward is in,
         not only in direct mode. `/subscribe` is the call that creates the
         customer, writes the number and records the text-marketing opt-in, and
         it carries a fallback endpoint for stores where the app proxy has never
         been created. `doubleDown.url` is the separate SMS-PROVIDER hop, which
         neither writes any consent nor exists on a proxy-less store — using it
         as the only path made this stage fail outright on this store with a 404
         while the customer work it was supposed to do never ran. It is still
         used as the path of last resort when there is no consent endpoint. */
      var viaConsent = direct || consentEndpoints().length > 0;
      if (!viaConsent && !config.doubleDown.url) {
        markSmsError(unavailableNumberMessage());
        return;
      }

      var phone = normalisePhone(el.phone ? el.phone.value : '');
      if (!phone.valid) {
        markSmsError(invalidNumberMessage());
        if (el.phone) {
          el.phone.setAttribute('aria-invalid', 'true');
          el.phone.focus({ preventScroll: true });
          scrollIntoView(el.phone);
        }
        track(EVENT_PREFIX + 'phone_invalid', { reason: phone.reason });
        return;
      }

      if (el.smsForm) el.smsForm.classList.remove('has-error');
      if (el.phone) el.phone.removeAttribute('aria-invalid');
      setMessage(el.smsMessage, '', false);
      setBusy(el.smsSubmit, true, null);
      setState(STATES.SMS_SUBMITTING);
      track(EVENT_PREFIX + 'phone_submit', { percent: game.percent });

      var result;
      if (viaConsent) {
        /* The number rides along on the same consent call as the email, so one
           profile ends up holding both — no captcha, and no proxy round trip for
           the reward itself. The mystery reward is the merchant's own copy rather
           than something a server hands back. */
        var sub = await subscribeRemote({
          email: (saved.claimed && saved.claimed.email) || '',
          phone: phone.e164,
          phoneE164: phone.e164
        });

        if (sub.ok && sub.phone !== true) {
          /* The address was taken and the number was not: for THIS stage that is
             a failed submission, so it must not flip the card. */
          var inUse = sub.phoneError === 'phone_in_use';
          result = {
            ok: false,
            message: (inUse ? inUseNumberMessage() : '') || sub.message || unavailableNumberMessage()
          };
          track(EVENT_PREFIX + 'phone_not_stored', { reason: sub.phoneError || sub.reason });
        } else {
          result = sub.ok
            ? { ok: true, data: { success: true, mystery: config.doubleDown.mystery || '' } }
            : { ok: false, message: consentFailureMessage(sub), reason: sub.reason };
        }
      } else {
        result = await postJson(config.doubleDown.url, {
          /* E.164, not what was typed: this hop has an SMS provider behind it. */
          phone: phone.e164,
          phoneE164: phone.e164,
          email: (saved.claimed && saved.claimed.email) || '',
          code: game.code,
          journey: game.outcome,
          marketingConsent: true,
          consentText: config.doubleDown.consent
        });
      }

      setBusy(el.smsSubmit, false, null);

      if (!result.ok || !result.data || result.data.success !== true) {
        /* Never claim a subscription that did not happen — and never blame the
           number for a failure that had nothing to do with it. The card stays
           face down, the reward and the game state are untouched, and the
           shopper can press again. */
        markSmsError(result.message || consentFailureMessage(result));
        return;
      }

      game.mystery = typeof result.data.mystery === 'string' ? result.data.mystery : '';
      saved = Core.writeState(store, {
        sms: { at: now(), mystery: game.mystery }
      });

      await revealMystery();
    }

    async function revealMystery() {
      setState(STATES.MYSTERY_REVEAL);
      if (el.mysteryCard) el.mysteryCard.classList.add('is-flipped');
      await wait(SPEED.flip);

      if (el.mysteryReward) {
        el.mysteryReward.textContent = game.mystery || '';
        el.mysteryReward.hidden = !game.mystery;
      }

      setHeader(config.mystery.heading, fmt(config.mystery.sub, { percent: game.percent }));
      setBlock('doubleDown', false);
      track(EVENT_PREFIX + 'mystery_reveal', { mystery: game.mystery || null });
      announce(config.mystery.heading);

      await wait(SPEED.autoAdvance);
      showFinal();
    }

    /* ------------------------------------------------------- the reward lock */

    /**
     * Has this shopper's reward already been decided?
     *
     * ONE SHOPPER, ONE RESULT. A resolved hand, a claimed reward and the durable
     * copies of either are all consulted, because the lock has to survive three
     * different kinds of return: a close and reopen on this page, a reload in
     * this session, and a brand-new tab tomorrow. `game.code` covers the claim,
     * `saved.result` the resolved hand, and `saved.claimed` a reward that was
     * banked before either field existed.
     *
     * The authoritative copy is the server's: a claim is keyed by address and
     * always answers with the FIRST reward that address won, so nothing here can
     * mint a second code. This half exists so the shopper SEES that reward
     * instead of a fresh hand — localStorage is not a security boundary and is
     * not being used as one.
     */
    function rewardLocked() {
      /* The theme editor gets to play as many hands as it likes: nothing is
         persisted there, so locking would only make the harness unusable. */
      if (config.testMode) return false;
      if (game.code) return true;
      if (saved.claimed && saved.claimed.code) return true;
      return Boolean(saved.result);
    }

    /**
     * Is the links row's "Deal again" the way back to the table right now?
     *
     * It used to be withdrawn the moment the reward was decided, which left a
     * shopper who had just lost — or who had simply not claimed yet — looking at
     * "Nah, I'm good" and nothing else. What changed is what the button DOES,
     * not whether it is offered: once the reward is decided it deals a hand for
     * FUN (see the click handler and `beginReplay`), which the worker scores like
     * any other and which can never mint, because a replay session is priced as
     * the claim the shopper already holds.
     *
     * Withdrawn in exactly one case: when the big play-again button is already
     * offering that same hand on the same screen. Two controls for one action is
     * a worse screen than a shorter row.
     */
    function applyLockUi() {
      if (!el.dealAgain) return;
      var playable = rewardLocked() || hasPermanentReward();
      var offered = playable && !game.playAgainOffered;
      el.dealAgain.hidden = !offered;
      if (offered) el.dealAgain.removeAttribute('aria-hidden');
      else el.dealAgain.setAttribute('aria-hidden', 'true');
    }

    /* --------------------------------------------------------- play again */

    /**
     * Does the shopper already OWN a reward?
     *
     * The one gate on playing again. `game.code` is a code the SERVER issued in
     * this session; `saved.claimed` is that same code remembered across visits.
     * A resolved hand on its own is NOT enough — until an address has claimed,
     * the first journey is still in progress, and offering a replay before it
     * would be a way to reroll the hand that decides the reward.
     */
    function hasPermanentReward() {
      return Boolean(game.code) || Core.canReplay(saved);
    }

    /**
     * Offer — or withdraw — the play-again button.
     *
     * Withdrawn the moment a replay is dealt (see resetForNewHand) and offered
     * again when the hand is over, so there is always exactly one way in and no
     * way to start a second hand over the top of a live one.
     */
    function setPlayAgain(show, resultText) {
      var allowed = Boolean(show) && hasPermanentReward();
      /* Remembered as state, so the links row can stand down while this button is
         the offer, on any screen and in any order the two are built. */
      game.playAgainOffered = allowed;
      if (el.playAgain) {
        el.playAgain.hidden = !allowed;
        el.playAgain.disabled = false;
        el.playAgain.setAttribute('aria-busy', 'false');
      }
      if (el.playAgainLabel) {
        el.playAgainLabel.textContent = (config.playAgain && config.playAgain.label) || '';
      }
      if (el.playAgainResult) {
        el.playAgainResult.textContent = allowed && resultText ? resultText : '';
        el.playAgainResult.hidden = !(allowed && resultText);
      }
      setBlock('playAgain', allowed);
      /* The two replay controls are one decision, so the links row is brought
         back into line whenever this one changes. */
      applyLockUi();
      if (allowed) revealReplaySoon();
    }

    /**
     * Put the replay offer inside the panel.
     *
     * Measured on a 390x844 phone: with the reward and the code on screen, the
     * offer sits ~100px BELOW the bottom edge of the sheet — present, and
     * invisible, which is the same thing as missing to a shopper. Aligning the
     * flow (what the reward stages do) is right for the reward itself; this is
     * the other half of it, and it only ever scrolls DOWN, so the code and the
     * COPY CODE button stay exactly where the shopper was reading them. No-op on
     * the inline page surface, which owns its own scroll.
     */
    function revealReplay() {
      var node = el.playAgain;
      if (!node || !el.scroll || inline || node.hidden) return;
      var box = el.scroll.getBoundingClientRect();
      var rect = node.getBoundingClientRect();
      /* The note under the button counts as part of the offer. */
      var note = el.playAgainNote ? el.playAgainNote.getBoundingClientRect() : rect;
      var over = Math.round(Math.max(rect.bottom, note.bottom) - box.bottom + 12);
      if (over <= 0) return;
      /* INSTANT ALWAYS — and that is a measurement, not a preference. On this
         container `behavior: 'smooth'` does not move it at all: called with
         `{ top: 200, behavior: 'smooth' }` at 390x844 it leaves scrollTop at 0 a
         second later, while the same call with `'auto'` lands immediately. The
         repaints around it — a block animating in, a card settling, a row
         appearing — are what cancel it, and they do not stop for the duration of
         an animation. This is why a small nudge was always applied instantly;
         the long correction now is too, because an offer the shopper cannot see
         is not a smoother offer. */
      el.scroll.scrollTo({ top: el.scroll.scrollTop + over, behavior: 'auto' });
    }

    /* After the frame the caller is still building: `setPlayAgain` runs before
       the stage's own `alignFlowTop`, and both scrolls are smooth, so the later
       one has to be the reveal.

       TWO passes, because one is not enough on a phone. MEASURED at 390x844: the
       first pass fires at +260ms with a correct target and the position does not
       hold — a row appearing, a block animating in and a smooth scroll already in
       flight are all things that can cancel it, and the shopper is then left with
       the button below the fold, which is the whole thing this exists to stop.
       The second pass is a no-op when the first one worked (the offer is already
       inside the panel, so there is nothing to correct) and is the correction
       when it did not. */
    function revealReplaySoon() {
      if (inline) return;
      window.setTimeout(revealReplay, REDUCED ? 0 : 260);
      window.setTimeout(revealReplay, REDUCED ? 0 : 760);
    }

    /** A replay is being dealt: the button is inert until the answer arrives. */
    function setPlayAgainBusy(busy) {
      if (el.playAgain) {
        el.playAgain.disabled = Boolean(busy);
        el.playAgain.setAttribute('aria-busy', busy ? 'true' : 'false');
      }
      if (el.playAgainLabel) {
        var copy = config.playAgain || {};
        el.playAgainLabel.textContent = busy ? copy.busy || copy.label : copy.label;
      }
    }

    /**
     * Show the shopper the reward they already have, and deal nothing.
     *
     * Every path that would start a new hand goes through this once the result
     * is locked: "Deal again", an inline reset, and the deferred first deal on a
     * visit that turns out to be a return. The cards come back from the durable
     * copy, the code and its percentage are repainted, and no game is started.
     */
    /* ONE REWARD PER SHOPPER, AND THEIRS IS SPENT.

       Instead of a code that will no longer redeem, point them at referrals
       (Loox: give £10, get £10). Nothing is minted and the game stays playable
       for fun; this only swaps what the code block says. */
    var REFER_URL = '/pages/refer-a-friend';
    function showReferral() {
      var box = blocks.code;
      if (!box) return;
      setHeader('You’ve used your reward', 'Give £10, get £10 when you refer a friend.');
      var voucher = box.querySelector('[data-runit-bj-code-box]');
      if (voucher) voucher.hidden = true;
      if (el.codeKicker) el.codeKicker.textContent = 'ONE REWARD PER SHOPPER';
      if (el.codeNote) {
        el.codeNote.textContent =
          'Your friend gets £10 off their order, and you get £10 off your next order over £50.';
      }
      var cta = box.querySelector('[data-runit-bj-shop]');
      if (cta) {
        cta.href = REFER_URL;
        cta.textContent = 'Refer a friend';
      }
      setBlock('final', false);
      setBlock('code', true);
      if (el.ddStart) el.ddStart.hidden = true;
      announce('You’ve used your reward. Refer a friend: give £10, get £10.');
      alignFlowTop();
      track(EVENT_PREFIX + 'referral_shown', { tier: game.tier });
    }

    /* Ask the worker whether a saved code has been spent. The code is the only
       thing sent; unknown or unreachable simply leaves the code on screen. */
    function checkRewardUsed(code) {
      var claimUrl = (config.claim && config.claim.url) || '';
      if (!code || !/\/claim$/.test(claimUrl)) return;
      fetch(claimUrl.replace(/\/claim$/, '/reward-status'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ code: code })
      })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (data) {
          if (data && data.used === true && game.code === code) showReferral();
        })
        .catch(function () {});
    }

    function restoreLockedView() {
      if (!game.player.length && saved.hand) renderSnapshot(saved.hand);
      if (saved.claimed && saved.claimed.code) {
        game.percent = saved.claimed.percent;
        game.code = saved.claimed.code;
        game.outcome = game.outcome || saved.claimed.result || null;
        game.tier = game.tier || saved.claimed.tier || null;
        setHeader(
          config.final.heading,
          fmt(config.final.sub, { percent: saved.claimed.percent, code: saved.claimed.code })
        );
        renderCode(saved.claimed.percent, saved.claimed.code, true);
        setBlock('result', false);
        setBlock('email', false);
        setBlock('code', true);
        setBlock('links', true);
        lockPlay();
        setState(STATES.FINAL);
        alignFlowTop();
        checkRewardUsed(saved.claimed.code);
        return;
      }
      if (!game.outcome && saved.result) {
        game.outcome = saved.result.outcome || null;
        game.tier = saved.result.tier || null;
        game.percent = saved.result.percent || null;
      }
      restoreResultStage();
    }

    /**
     * Repaint a finished hand's reward stage from the saved snapshot.
     *
     * The tail of `resolve()`, minus everything with a side effect: the outcome
     * is READ back rather than re-decided, the played-hands count is untouched,
     * no analytics event is re-fired and no code is earned a second time. A
     * shopper who closed on “10% OFF” comes back to the same table and the same
     * offer instead of a new hand.
     */
    function restoreResultStage() {
      applyLockUi();
      var copy = config.results[game.outcome] || config.results.lose;
      setHeader(copy.heading, copy.sub);
      setState(STATES.RESULT);
      lockPlay();
      paintResultLine();
      if (el.codeKicker) el.codeKicker.textContent = config.claim.codeKicker;
      renderRewardCopy();
      setBlock('result', true);
      announce(copy.heading + ' ' + copy.sub);
      setBlock('email', true);
      setState(STATES.EMAIL_CAPTURE);
      setBlock('links', true);
    }

    /* ------------------------------------------------------------ open/close */

    function isInDesignMode() {
      return Boolean(window.Shopify && window.Shopify.designMode);
    }

    /* The storefront's overlay coordinator owns one question: may this feature
       show itself right now? It answers with the page allowlist AND whether a
       higher-priority overlay (cookie consent, wishlist, cart, quick-add) is
       up. Everything below only READS that verdict — nothing about the game,
       its state or its storage is decided here. */
    function overlays() {
      return window.RunItOverlays || null;
    }

    function pageAllows() {
      var api = overlays();
      return !api || api.allowed !== false;
    }

    /* Surfaces that make an ALREADY OPEN popup step aside. A wishlist toast is
       not one of them: it is a strip the popup would simply cover, and hiding
       the game for it would flicker on every heart tap. */
    var SUSPENDING = ['consent', 'wishlist', 'cart', 'quickAdd', 'modal', 'menu', 'dialog'];

    function suspensionReason() {
      var api = overlays();
      if (!api || !api.blockingKeys) return null;
      var keys = api.blockingKeys();
      for (var i = 0; i < keys.length; i++) {
        if (SUSPENDING.indexOf(keys[i]) !== -1) return keys[i];
      }
      return null;
    }

    /* Hide without closing: the hand, the reward, the code and the stored
       state all survive, and no dismissal is recorded, because the shopper did
       not dismiss anything — a higher-priority surface needed the screen. */
    function suspend(reason) {
      if (inline || root.hidden || !root.classList.contains('is-open')) return;
      if (game.suspended) return;
      game.suspended = true;
      game.suspendedBy = reason;
      root.setAttribute('data-runit-bj-suspended', reason || 'true');
      /* Release the scroll lock so the surface that took over can scroll, and
         so a cookie banner near the bottom of the viewport is reachable. */
      lockScroll(false);
    }

    function resume() {
      if (!game.suspended) return;
      game.suspended = false;
      game.suspendedBy = null;
      root.removeAttribute('data-runit-bj-suspended');
      if (!inline && root.classList.contains('is-open')) lockScroll(true);
    }

    function syncSuspension() {
      var reason = suspensionReason();
      if (reason) suspend(reason);
      else if (game.suspended) resume();
    }

    /* The floating trigger is the LOWEST priority persistent element on the
       page: while anything above it is up it disappears rather than moving or
       layering. It also waits for the consent verdict, so it cannot flash up
       behind a first-visit cookie screen (see runit-overlays.js). */
    function ctaAllowed() {
      if (!config.reopenTab || inline) return false;
      var api = overlays();
      if (api && !api.allowed) return false;
      if (api && typeof api.isBlocking === 'function' && api.isBlocking()) return false;
      return true;
    }

    function applyCta() {
      if (!el.tab) return;
      var open = !root.hidden && root.classList.contains('is-open');
      el.tab.hidden = open || !ctaAllowed();
    }

    function lockScroll(locked) {
      var html = document.documentElement;
      if (locked) {
        var gutter = window.innerWidth - html.clientWidth;
        if (gutter > 0) html.style.setProperty('--rl-bj-gutter', gutter + 'px');
        html.style.paddingRight = gutter > 0 ? gutter + 'px' : '';
        html.classList.add('rl-bj-lock');
      } else {
        html.classList.remove('rl-bj-lock');
        html.style.paddingRight = '';
        html.style.removeProperty('--rl-bj-gutter');
      }
    }

    function open(reason) {
      if (inline) {
        if (game.state === STATES.CLOSED || game.state === STATES.READY) deal();
        return;
      }
      if (!root.hidden && root.classList.contains('is-open')) return;
      window.clearTimeout(game.autoTimer);

      game.lastFocus = document.activeElement;
      root.hidden = false;
      if (el.tab) el.tab.hidden = true;
      if (game.suspended) resume();
      lockScroll(true);

      window.requestAnimationFrame(function () {
        root.classList.add('is-open');
      });

      /* Move focus into the dialog when the SHOPPER opened it, so the keyboard
       * follows them and a screen reader announces the dialog. Deliberately
       * not on the auto-open: stealing focus on page load is hostile. */
      if (reason && reason !== 'auto' && el.close) {
        window.setTimeout(function () {
          try {
            el.close.focus({ preventScroll: true });
          } catch (e) {
            /* noop */
          }
        }, REDUCED ? 40 : 280);
      }

      /* Read the previous state before overwriting it — this decides whether
         the visit starts a fresh hand or resumes the one in progress. */
      var freshVisit = game.state === STATES.CLOSED || game.state === STATES.FINAL;

      setState(STATES.READY);
      if (!config.testMode) {
        saved = Core.writeState(store, {
          seen: true,
          views: Core.readState(store).views + 1,
          firstSeenAt: Core.readState(store).firstSeenAt || now()
        });
      }

      track(EVENT_PREFIX + 'popup_view', { reason: reason || 'auto' });

      /* The hand comes back FIRST, and independently of the reward state:
         reopening onto “YOU'RE IN” with an empty table is the bug this exists to
         prevent. `restoreHand` repaints from the saved snapshot rather than from
         whatever the DOM happens to still hold, so it is the same on the second
         reopen as on the first, and it empties the table before it draws — a
         reopened hand can never show a duplicated card. It returns null when
         there is nothing to restore, and only then does a fresh hand get dealt. */
      var hand = restoreHand();
      /* A new tab has no session hand. The durable copy that travels with a
         resolved result repaints the table, so a returning shopper does not find
         "10% OFF" over a blank board — and the durable RESULT is what stops that
         visit from dealing itself a second, better hand. */
      if (!hand && saved.result && saved.hand) hand = renderSnapshot(saved.hand);
      var restore = saved.claimed && saved.claimed.code ? saved.claimed : null;

      /* Still being played: HIT and STAND pick up exactly where they left off,
         on the same cards and the same totals. This wins over the reward screen
         because the hand in progress is the last thing that was on the table —
         even when an earlier hand already banked a code, which is exactly how a
         shopper plays on after claiming. */
      if (hand === 'player') {
        /* The cached table paints instantly so a reopen looks like a reopen, and
           the worker is then asked what the hand actually is — another tab may
           have finished it while this one was closed. */
        game.busy = false;
        setState(STATES.PLAYER_TURN);
        unlockControls(true);
        announce('Your total is ' + Core.handTotal(game.player).total + '. Hit or stand.');
        reconcileWithServer();
        focusClose();
        return;
      }

      if (restore) {
        /* A new tab has no session hand, so the finished hand is stored WITH the
           reward too and repainted from there. The reward is durable, so the
           table it was won on has to be — otherwise “10% OFF UNLOCKED” returns
           over an empty board, which is the reported bug in its other form. */
        if (!hand) renderSnapshot(restore.hand);
        game.percent = restore.percent;
        game.code = restore.code;
        game.outcome = restore.result || game.outcome;
        game.tier = restore.tier || game.tier || 'win';
        setHeader(config.final.heading, fmt(config.final.sub, { percent: restore.percent }));
        renderCode(restore.percent, restore.code, true);
        /* The reward is already unlocked, so the claim stage is behind the
           shopper now: the code and the finished hand, nothing to fill in. */
        setBlock('result', false);
        setBlock('email', false);
        setBlock('code', true);
        setBlock('links', true);
        lockPlay();
        setState(STATES.FINAL);
        focusClose();
        return;
      }

      /* Finished, reward not yet unlocked: the final cards, the result and the
         offer the shopper was reading. */
      if (hand === 'done') {
        restoreResultStage();
        /* The cached result is painted first, then corrected: localStorage is not
           allowed to be the authority on what a hand was worth, so the worker is
           asked and its answer is what stands. */
        reconcileWithServer();
        focusClose();
        return;
      }

      /* Resolved on an earlier visit, with no cards left to repaint: the result
         still stands, so it is shown rather than re-dealt. */
      if (saved.result && !hand) {
        game.outcome = saved.result.outcome || null;
        game.tier = saved.result.tier || null;
        game.percent = saved.result.percent || null;
        restoreResultStage();
        reconcileWithServer();
        focusClose();
        return;
      }

      if (freshVisit) {
        /* ...but never a second hand for a shopper who already has one. */
        if (rewardLocked()) {
          restoreLockedView();
          focusClose();
          return;
        }
        /* A session id with no cached hand is a hand in progress that this tab
           did not paint — a new tab, or a cleared sessionStorage. It is asked
           about before a new hand is dealt, so the shopper resumes the hand the
           server already has instead of rolling a second one. */
        if (saved.sessionId) {
          setState(STATES.DEALING);
          reconcileWithServer().then(function (verdict) {
            if (verdict === 'gone') window.setTimeout(deal, SPEED.step);
            else if (verdict === 'unknown') serverProblem({ reason: 'network' });
          });
          return;
        }
        window.setTimeout(deal, SPEED.step);
      } else {
        focusClose();
      }
    }

    function close(reason) {
      if (inline) {
        /* An inline game never disappears — a fresh hand is the equivalent. */
        track(EVENT_PREFIX + 'close', { reason: reason || 'inline_reset', inline: true });
        deal();
        return;
      }
      if (root.hidden) return;
      window.clearTimeout(game.autoTimer);
      window.clearTimeout(game.advanceTimer);

      root.classList.remove('is-open');
      if (el.tab) applyCta();

      if (!config.testMode) {
        var snapshot = saved;
        if (snapshot.claimed && snapshot.claimed.code) {
          game.hardDismissed = true;
        } else {
          saved = Core.writeState(store, {
            lastDismissedAt: now(),
            dismissals: Core.readState(store).dismissals + 1
          });
        }
      }

      track(EVENT_PREFIX + 'close', {
        reason: reason || 'user',
        stage: game.state,
        claimed: Boolean(game.code)
      });

      unlockControls(false);
      setState(STATES.CLOSED);

      window.setTimeout(function () {
        root.hidden = true;
        root.classList.remove('is-open');
      }, REDUCED ? 30 : 260);

      lockScroll(false);

      /* Back where they were: the trigger if it is on screen (that is the
       * control they pressed), otherwise whatever had focus before. */
      var restore = null;
      if (el.tab && !el.tab.hidden && el.tab.getClientRects().length) restore = el.tab;
      else if (game.lastFocus && document.contains(game.lastFocus)) restore = game.lastFocus;
      if (restore && typeof restore.focus === 'function') {
        try {
          restore.focus({ preventScroll: true });
        } catch (e) {
          /* noop */
        }
      }
      game.lastFocus = null;
    }

    /* --------------------------------------------------------------- wiring */

    function focusables() {
      return qa(
        'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])'
      ).filter(function (node) {
        /* getClientRects survives position:fixed ancestors, which null out offsetParent. */
        return node.getClientRects().length > 0;
      });
    }

    function onKeydown(event) {
      if (inline || root.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        close('escape');
        return;
      }

      if (event.key !== 'Tab') return;

      var list = focusables();
      if (!list.length) return;
      var first = list[0];
      var last = list[list.length - 1];
      var active = document.activeElement;

      if (event.shiftKey && (active === first || !root.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !root.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    function onCopy(percent, code, button) {
      if (!code) return;
      var done = function () {
        if (button) {
          /* Kept on the element so a second tap inside the window cannot make
             "Copied" the text it goes back to. */
          var original = button.getAttribute('data-rest') || button.textContent;
          button.setAttribute('data-rest', original);
          button.classList.add('is-done');
          button.textContent = config.claim.copyDone;
          window.setTimeout(function () {
            button.classList.remove('is-done');
            button.removeAttribute('data-rest');
            button.textContent = original;
          }, 1800);
        }
        announce(config.claim.copyDone);
      };
      var fallback = function () {
        try {
          var helper = document.createElement('textarea');
          helper.value = code;
          helper.setAttribute('readonly', '');
          helper.style.position = 'fixed';
          helper.style.opacity = '0';
          document.body.appendChild(helper);
          helper.select();
          document.execCommand('copy');
          document.body.removeChild(helper);
          done();
        } catch (e) {
          /* Clipboard unavailable — the code is on screen to read. */
        }
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(done).catch(fallback);
      } else {
        fallback();
      }
    }

    function bind() {
      if (el.close) el.close.addEventListener('click', function () {
        close('close-button');
      });
      if (el.overlay) el.overlay.addEventListener('click', function () {
        close('overlay');
      });
      if (el.hit) el.hit.addEventListener('click', hit);
      if (el.stand) el.stand.addEventListener('click', stand);

      if (el.form) {
        el.form.addEventListener('submit', function (event) {
          event.preventDefault();
          claim();
        });
      }

      if (el.smsForm) {
        el.smsForm.addEventListener('submit', submitSms);
      }
      if (el.ddStart) {
        el.ddStart.addEventListener('click', function () {
          openDoubleDown(true);
        });
      }
      if (el.ddSkip) {
        el.ddSkip.addEventListener('click', function () {
          track(EVENT_PREFIX + 'shop_click', { from: 'double_down_skip', percent: game.percent });
          openFinal();
        });
      }
      if (el.playAgain) {
        el.playAgain.addEventListener('click', function () {
          /* `beginReplay` guards a double tap as well; this is the cheap first
             line, and it keeps a busy hand from even logging a second press. */
          if (game.busy) return;
          track(EVENT_PREFIX + 'play_again', { percent: game.percent });
          beginReplay();
        });
      }
      if (el.dealAgain) {
        el.dealAgain.addEventListener('click', function () {
          track(EVENT_PREFIX + 'deal_again', {
            claimed: Boolean(game.code),
            locked: rewardLocked()
          });
          /* THE REWARD IS DECIDED: this is a hand played FOR FUN, and it is
             dealt as a replay so the worker prices it as the claim the shopper
             already holds or is about to. `deal()` here would be a re-roll of
             the hand that decides the reward, and it refuses on its own —
             going through `beginReplay` says plainly which one this is. */
          if (rewardLocked()) {
            beginReplay();
            return;
          }
          deal();
        });
      }
      if (el.decline) {
        el.decline.addEventListener('click', function () {
          close('declined');
        });
      }
      if (el.copy) {
        el.copy.addEventListener('click', function () {
          onCopy(game.percent, game.code, el.copy);
        });
      }
      /* `data-runit-bj-code` / `data-runit-bj-final-code` are the code's TEXT and
         carry no handler: reading a code and copying one are two different
         actions, and only the COPY CODE buttons below offer the second. */
      /* The voucher is the copy control: a tap anywhere on it copies the code
         and "Copied" is written over its meta line, never over the code. */
      if (el.codeBox) {
        el.codeBox.addEventListener('click', function () {
          onCopy(game.percent, game.code, el.codeMeta);
        });
      }
      if (el.finalCodeBox) {
        el.finalCodeBox.addEventListener('click', function () {
          onCopy(game.percent, game.code, el.finalMeta);
        });
      }
      if (el.finalCopy) {
        el.finalCopy.addEventListener('click', function () {
          onCopy(game.percent, game.code, el.finalCopy);
        });
      }

      var shopLinks = qa('[data-runit-bj-shop], [data-runit-bj-final-shop]');
      shopLinks.forEach(function (link) {
        link.setAttribute('href', shopUrl);
        link.addEventListener('click', function () {
          track(EVENT_PREFIX + 'shop_click', { percent: game.percent });
        });
      });

      document.addEventListener('keydown', onKeydown);
    }

    /* Shopify's preview bar is pinned to the bottom edge and would cover the
       trigger while the merchant previews the theme, so lift it clear. The bar
       is injected asynchronously, hence the short bounded poll. */
    function clearPreviewBar() {
      if (!el.tab) return;
      var tries = 0;
      var apply = function () {
        var bar = document.getElementById('preview-bar-iframe');
        el.tab.classList.toggle('rl-bj-tab--preview', Boolean(bar));
        if (bar || ++tries > 40) {
          window.clearInterval(timer);
        }
      };
      var timer = window.setInterval(apply, 500);
      apply();
    }

    function scheduleAutoOpen() {
      if (inline) return;
      if (config.mode !== 'enabled' && config.mode !== 'testing') return;
      if (config.testMode) {
        window.setTimeout(function () {
          open('test');
        }, 600);
        return;
      }
      if (isInDesignMode()) return;

      var allowed = Core.shouldAutoOpen(saved, {
        now: now(),
        cooldownDays: config.cooldownDays
      });
      if (!allowed) return;

      var delay = Math.max(0, Number(config.delay) || 0) * 1000;
      game.autoTimer = window.setTimeout(function () {
        /* The timer is a moment, not a promise: if a cookie screen, the cart or
           anything else has the screen when it fires, the popup does NOT open
           afterwards either. The shopper keeps the small CTA and decides. No
           dismissal is recorded, because nothing was dismissed. */
        if (!pageAllows() || suspensionReason() || game.consentSeen) return;
        var api = overlays();
        if (api && typeof api.isBlocking === 'function' && api.isBlocking()) return;
        open('auto');
      }, delay);
    }

    bind();

    if (inline) {
      /* Already on screen: make it visible and deal the opening hand. */
      root.classList.add('is-open');
      setState(STATES.READY);
      window.setTimeout(deal, SPEED.reveal);
    } else {
      scheduleAutoOpen();
    }

    if (el.tab) {
      applyCta();
      el.tab.addEventListener('click', function () {
        open('reopen_tab');
      });
      clearPreviewBar();
    }

    /* One subscription for the whole feature: the coordinator decides whether
       the trigger may show, and whether an open popup must step aside. Bound
       through a named function so a coordinator that arrives late (a section
       reload can re-inject either script first) is still picked up, instead of
       leaving a pill that nothing can hide. */
    function syncFromOverlays(detail) {
      if (!pageAllows()) {
        /* Not a dismissal and not a close: the page simply does not offer the
           game once the coordinator says so (an ajax nav onto a policy page,
           for instance). Nothing is written to storage. */
        window.clearTimeout(game.autoTimer);
        if (!root.hidden) {
          root.classList.remove('is-open');
          root.hidden = true;
          root.setAttribute('data-runit-bj-suspended', 'page');
          game.suspended = true;
          lockScroll(false);
        }
        applyCta();
        return;
      }
      /* A cookie banner is actually on screen: that is the shopper's first
         question, and it is not "do you want to play blackjack". Answering it
         must not be followed by the game throwing itself open. */
      if (detail && detail.state && detail.state.consent === 'open' && !game.consentSeen) {
        game.consentSeen = true;
        window.clearTimeout(game.autoTimer);
      }
      syncSuspension();
      applyCta();
    }

    function bindOverlays() {
      var api = overlays();
      if (!api || typeof api.subscribe !== 'function') return false;
      if (game.overlapsBound === api) return true;
      game.overlapsBound = api;
      api.subscribe(syncFromOverlays);
      syncFromOverlays(null);
      return true;
    }

    if (!bindOverlays()) {
      document.addEventListener('runit:overlays-ready', bindOverlays);
    }

    /* Belt and braces with the Liquid gate: a page that is not on the allowlist
       never runs the popup, even if its markup somehow reached the DOM. */
    if (!inline && !pageAllows()) {
      window.clearTimeout(game.autoTimer);
      root.hidden = true;
      if (el.tab) el.tab.hidden = true;
      return {
        open: function () {},
        close: function () {},
        deal: function () {},
        inline: false,
        root: root,
        config: config,
        state: function () { return STATES.CLOSED; }
      };
    }

    return {
      open: open,
      close: close,
      deal: deal,
      inline: inline,
      root: root,
      config: config,
      state: function () {
        return game.state;
      }
    };
  }

  /* ------------------------------------------------------------------ *
   * Boot — one instance per root, guarded against double initialisation
   * (theme editor section reloads) and never in the checkout editor.
   * ------------------------------------------------------------------ */

  var instances = [];

  function init() {
    var roots = document.querySelectorAll('[data-runit-bj]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.getAttribute('data-runit-bj-init') === 'true') return;
      root.setAttribute('data-runit-bj-init', 'true');
      var instance = createGame(root);
      if (instance) {
        instances.push(instance);
        window.RunItBlackjacks = instances;
        if (!window.RunItBlackjack) window.RunItBlackjack = instance;
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor: a section reload replaces the DOM, so pick up the new root.
     Guarded by the per-root init flag, so repeated events are harmless. */
  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (!target || !target.querySelector) return;
    if (target.matches('[data-runit-bj]') || target.querySelector('[data-runit-bj]')) init();
  });
})();
